import {
  DEFAULT_LOCALE,
  isSupportedLocale,
  type SupportedLocale,
} from '@/i18n/config';
import { createVietnameseStaticCopy } from '@/i18n/vietnamese-static-copy';

type ProductStaticCopy = {
  workspace: {
    mediaStudio: string;
    catalogueData: string;
    printsAndPatterns: string;
    technicalBlueprints: string;
    /**
     * The org-chat product panel's virtual manufacturing tab. Distinct from
     * `technicalBlueprints`, which names the product page's own tab: renaming
     * that one would relabel a different surface.
     */
    manufacturing: string;
    threeDModels: string;
    creating: string;
    newChat: string;
    chatWithThisProduct: string;
    newTab: string;
    chats: string;
    failedToProcessMessage: string;
    askAnythingOrMentionAsset: string;
    main: string;
    mainAsset: string;
    generatingImage: string;
    productListing: string;
    socialMedia: string;
    lifestyle: string;
    detail: string;
    layoutView: string;
    groupedView: string;
    allAssetsView: string;
    listView: string;
    exitSelectionMode: string;
    selecting: string;
    selectAssets: string;
    select: string;
    selectedCount: string;
    clearSelection: string;
    selectAll: string;
    clear: string;
    allCount: string;
    downloadSelected: string;
    preparing: string;
    download: string;
    mentionSelectedInChat: string;
    mention: string;
    deleteSelected: string;
    delete: string;
    editAsset: string;
    viewFullScreen: string;
    deleteAsset: string;
    deleteThisAsset: string;
    selectAsset: string;
    openGenerationChat: string;
    chooseAssetToDiscuss: string;
    mainAssetAlt: string;
    version: string;
    noVersionSelected: string;
    noMainAsset: string;
    selected: string;
    newLabel: string;
    moreVersionsAvailable: string;
    selectedAsset: string;
    videoAsset: string;
    threeDModel: string;
    replaceAsset: string;
    maximizeAsset: string;
    minimizeAsset: string;
    assetPreview: string;
    imageLabel: string;
    openAsset: string;
    selectFileForDownload: string;
    deselectFile: string;
    createdRelative: string;
    updatedRelative: string;
    generatingVideo: string;
    fineTuningImage: string;
    createButton?: string;
    createImageButton?: string;
    createVideoButton?: string;
    imageGenerationStarted?: string;
    imageGenerationFailed?: string;
    videoGenerationStarted?: string;
    videoGenerationFailed?: string;
    videoGenerationLoading?: string;
    addMainProductImageBeforeVideo?: string;
    noAssetsFound?: string;
    changeSelectionWhilePinned: string;
    selectVersionBeforePreparingDownload: string;
    selectAtLeastOneFile: string;
    preparingFilesForDownload: string;
    addedAssetsToChat: string;
    deleteAssetsTitle: string;
    deleteAssetsDescription: string;
    deletingAssets: string;
    deletedAssets: string;
    somethingWentWrong: string;
    changeLayoutView: string;
    deleteAssetQuestion: string;
    deletingAsset: string;
    assetDeleted: string;
    generatingAdditionalAssets: string;
    creatingMoreAssets: string;
    generatingMainAsset: string;
    logoReference?: {
      title: string;
      description: string;
      autoBadge: string;
      manualBadge: string;
      addManual: string;
      editManual: string;
      reextract: string;
      processing: string;
      noSavedReference: string;
      saveFailed: string;
      missingRequiredFields?: string;
      missingImage?: string;
      uploadFailed: string;
      logoTextLabel: string;
      logoTextPlaceholder: string;
      contextLabel: string;
      contextPlaceholder: string;
    };
  };
  avatarSelector: {
    failedToSaveAvatarSelection: string;
    failedToRemoveAvatar: string;
    publishedIndividually: string;
    createAvatarCharacterPrompt: string;
    addAvatar: string;
    editAvatars: string;
    createAvatars: string;
    createAvatarsDescription: string;
    goToDesign: string;
    useAvatars: string;
    useAvatarsDescription: string;
    on: string;
    off: string;
    avatarsEnabledDescription: string;
    avatarsDisabledDescription: string;
    selectAvatars: string;
    selectAvatarsDescription: string;
    searchAvatars: string;
    noAvatarsFound: string;
    tryDifferentSearchTerm: string;
    noAvatarsAvailable: string;
    createAvatarsFirst: string;
    yourWorkspace: string;
    sharedWithYou: string;
    privateAndPublished: string;
    private: string;
    publishedSets: string;
    verifiedPublishers: string;
    avatarCount: string;
    selected: string;
    cancel: string;
    save: string;
    noChanges: string;
    publishedByWorkspace: string;
    verifiedWorkspace: string;
    failed: string;
    queued: string;
    generating: string;
    removeAvatar: string;
    avatarSeedPrompts: string[];
  };
  generationSuggestions: {
    generatingAssets: string;
    starting: string;
    stoppedByUser: string;
    regenerateSuggestions: string;
    regenerateSuggestionsDescription: string;
    regenerate: string;
    cancel: string;
    runAllSuggestions: string;
    runAllSuggestionsDescription: string;
    fullAutoMode: string;
    fullAutoModeDescription: string;
    startFullAutoMode: string;
    imageSingular: string;
    imagePlural: string;
    numberOfImagesToGenerate: string;
    generateBetween: string;
    editSuggestion: string;
    maxImagesSavedForSuggestion: string;
    savesDefaultMaxForFutureRuns: string;
    saveDefaults: string;
    aiSuggestions: string;
    generatingImageCount: string;
    stopGeneration: string;
    runAll: string;
    fullAuto: string;
    analyzingProductAndGeneratingSuggestions: string;
    noSuggestionsYet: string;
    noSuggestionsDescription: string;
    generateSuggestions: string;
    failedToLoadSuggestions: string;
    generateImageCountCta: string;
    referenceOnlyVideoTitle?: string;
    referenceOnlyVideoDescription?: string;
    referenceOnlyVideoGenerating?: string;
    referenceOnlyVideoStarted?: string;
    failedToStartVideoGeneration?: string;
  };
  walkthrough: {
    productSuggestionsTitle: string;
    productSuggestionsDescription: string;
    productAvatarsTitle: string;
    productAvatarsDescription: string;
    productTabsTitle: string;
    productTabsDescription: string;
    productGalleryTitle: string;
    productGalleryDescription: string;
    productAddAssetTitle: string;
    productAddAssetDescription: string;
    productChatTabsTitle: string;
    productChatTabsDescription: string;
    productChatNewTitle: string;
    productChatNewDescription: string;
    productChatInputTitle: string;
    productChatInputDescription: string;
    productVersionsTitle: string;
    productVersionsDescription: string;
    mobileProductTabsTitle: string;
    mobileProductTabsDescription: string;
    mobileProductChatTitle: string;
    mobileProductChatDescription: string;
  };
};

const AVATAR_SEED_PROMPTS = [
  'A friendly barista with a warm smile, photorealistic portrait',
  'A confident startup founder, clean minimal outfit, studio lighting',
  'A cozy bookshop owner, soft natural light, cinematic look',
  'A stylish creative director, fashion editorial vibe, high detail',
  'A calm yoga instructor, serene expression, neutral tones',
  'A playful gamer, subtle neon accents, modern streetwear',
  'A thoughtful professor, glasses, warm and trustworthy',
  'A modern designer, monochrome outfit, clean background',
];

const EN_COPY: ProductStaticCopy = {
  workspace: {
    mediaStudio: 'Media Studio',
    catalogueData: 'Catalogue Data',
    printsAndPatterns: 'Prints & Patterns',
    technicalBlueprints: 'Technical Studio',
    manufacturing: 'Manufacturing',
    threeDModels: '3D Models',
    creating: 'Creating...',
    newChat: 'New Chat',
    chatWithThisProduct: 'Chat with this product',
    newTab: 'New tab',
    chats: 'Chats',
    failedToProcessMessage: 'Failed to process message',
    askAnythingOrMentionAsset: 'Ask or @mention',
    main: 'Main',
    mainAsset: 'Main asset',
    generatingImage: 'Generating image',
    productListing: 'Product Listing',
    socialMedia: 'Social Media',
    lifestyle: 'Lifestyle',
    detail: 'Detail',
    layoutView: 'Layout view',
    groupedView: 'Grouped view',
    allAssetsView: 'All assets view',
    listView: 'List view',
    exitSelectionMode: 'Exit selection mode',
    selecting: 'Selecting',
    selectAssets: 'Select assets',
    select: 'Select',
    selectedCount: '{count} selected',
    clearSelection: 'Clear selection',
    selectAll: 'Select all',
    clear: 'Clear',
    allCount: 'All ({count})',
    downloadSelected: 'Download selected',
    preparing: 'Preparing...',
    download: 'Download',
    mentionSelectedInChat: 'Mention selected in chat',
    mention: 'Mention',
    deleteSelected: 'Delete selected',
    delete: 'Delete',
    editAsset: 'Edit asset',
    viewFullScreen: 'View full screen',
    deleteAsset: 'Delete asset',
    deleteThisAsset: 'Delete this asset?',
    selectAsset: 'Select Asset',
    openGenerationChat: 'Open generation chat',
    chooseAssetToDiscuss: 'Choose an asset to discuss in the chat',
    mainAssetAlt: '{productName} main asset',
    version: 'Version',
    noVersionSelected: 'No version selected',
    noMainAsset: 'No main asset',
    selected: 'Selected',
    newLabel: 'New',
    moreVersionsAvailable: '{count} more versions available',
    selectedAsset: 'Selected Asset',
    videoAsset: 'Video Asset',
    threeDModel: '3D Model',
    replaceAsset: 'Replace asset',
    maximizeAsset: 'Maximize asset',
    minimizeAsset: 'Minimize asset',
    assetPreview: 'Asset preview',
    imageLabel: 'Image',
    openAsset: 'Open asset',
    selectFileForDownload: 'Select file for download',
    deselectFile: 'Deselect file',
    createdRelative: 'Created {time}',
    updatedRelative: 'Updated {time}',
    generatingVideo: 'Generating video',
    fineTuningImage: 'Fine tuning image',
    createButton: 'Create',
    createImageButton: 'Create image',
    createVideoButton: 'Create video',
    imageGenerationStarted: 'Image generation started',
    imageGenerationFailed: 'Could not start image generation',
    videoGenerationStarted: 'Video generation started',
    videoGenerationFailed: 'Could not start video generation',
    videoGenerationLoading: 'Generating video...',
    addMainProductImageBeforeVideo:
      'Add a main product image before creating a video',
    noAssetsFound: 'No assets yet',
    changeSelectionWhilePinned:
      'Cannot change selection while asset is pinned. Unpin first.',
    selectVersionBeforePreparingDownload:
      'Select a version before preparing a download.',
    selectAtLeastOneFile: 'Select at least one file.',
    preparingFilesForDownload: 'Preparing {count} files for download',
    addedAssetsToChat: 'Added {count} assets to chat',
    deleteAssetsTitle: 'Delete assets?',
    deleteAssetsDescription:
      'This action cannot be undone. The selected assets will be permanently deleted.',
    deletingAssets: 'Deleting {count} assets...',
    deletedAssets: 'Deleted {count} assets',
    somethingWentWrong: 'Something went wrong',
    changeLayoutView: 'Change layout view',
    deleteAssetQuestion: 'Delete this asset?',
    deletingAsset: 'Deleting asset...',
    assetDeleted: 'Asset deleted',
    generatingAdditionalAssets: 'Generating additional assets...',
    creatingMoreAssets: 'Creating more assets for your product',
    generatingMainAsset: 'Generating main asset...',
    logoReference: {
      title: 'Logo Reference',
      description:
        'Keep a reusable logo reference here so future generations preserve the right mark, placement cues, and color treatment.',
      autoBadge: 'Auto',
      manualBadge: 'Manual',
      addManual: 'Upload your logo',
      editManual: 'Upload your logo',
      reextract: 'Re-run extraction',
      processing: 'Generating logo reference.',
      noSavedReference: 'No saved logo',
      saveFailed: 'Failed to save logo reference.',
      missingRequiredFields:
        'Please add a logo title and description before saving.',
      missingImage: 'Please upload a logo image before saving.',
      uploadFailed: 'Failed to upload logo reference.',
      logoTextLabel: 'Logo text',
      logoTextPlaceholder: 'Brand wordmark or logo text',
      contextLabel: 'Context',
      contextPlaceholder:
        'Example: small tonal cream embroidery on the upper-left chest over ribbed black knit',
    },
  },
  avatarSelector: {
    failedToSaveAvatarSelection: 'Failed to save avatar selection',
    failedToRemoveAvatar: 'Failed to remove avatar',
    publishedIndividually: 'Published individually',
    createAvatarCharacterPrompt: 'Create an avatar character: {seed}',
    addAvatar: 'Add Avatar',
    editAvatars: 'Edit avatars',
    createAvatars: 'Create avatars',
    createAvatarsDescription:
      'Avatars let you generate lifestyle and social shots with a consistent character.',
    goToDesign: 'Go to Design',
    useAvatars: 'Use avatars',
    useAvatarsDescription:
      'Pick up to 5 avatars to guide lifestyle and social image generations.',
    on: 'On',
    off: 'Off',
    avatarsEnabledDescription:
      'Selected avatars will be used for lifestyle and social shots.',
    avatarsDisabledDescription:
      'Avatars are disabled. Toggle on to use them for generations.',
    selectAvatars: 'Select avatars',
    selectAvatarsDescription:
      'Choose up to 5 workspace avatars for lifestyle and social shots.',
    searchAvatars: 'Search avatars...',
    noAvatarsFound: 'No avatars found',
    tryDifferentSearchTerm: 'Try a different search term',
    noAvatarsAvailable: 'No avatars available',
    createAvatarsFirst: 'Create avatars in the Avatars section first.',
    yourWorkspace: 'Your workspace avatars',
    sharedWithYou: 'Shared avatars',
    privateAndPublished: 'Private and published',
    private: 'Private avatars',
    publishedSets: 'Published sets',
    verifiedPublishers: 'Verified publishers',
    avatarCount: '{count} avatars',
    selected: 'Selected:',
    cancel: 'Cancel',
    save: 'Save',
    noChanges: 'No changes',
    publishedByWorkspace: 'Published by {workspaceName}',
    verifiedWorkspace: 'Verified workspace',
    failed: 'Failed',
    queued: 'Queued',
    generating: 'Generating',
    removeAvatar: 'Remove {name}',
    avatarSeedPrompts: AVATAR_SEED_PROMPTS,
  },
  generationSuggestions: {
    generatingAssets: 'Generating assets',
    starting: 'Starting...',
    stoppedByUser: 'Stopped by user',
    regenerateSuggestions: 'Regenerate suggestions',
    regenerateSuggestionsDescription:
      'This will analyze your product again and generate new suggestions based on the current main image and gallery. Your existing suggestions will be replaced.',
    regenerate: 'Regenerate',
    cancel: 'Cancel',
    runAllSuggestions: 'Run all suggestions',
    runAllSuggestionsDescription:
      'This will run all suggestions ({totalImages} images total). It may take some time, but assets will appear as they finish.',
    fullAutoMode: 'Full auto mode',
    fullAutoModeDescription:
      'This will automatically decide what to generate next based on your product’s main image and existing gallery, then start generating assets immediately.',
    startFullAutoMode: 'Start full auto mode',
    imageSingular: 'image',
    imagePlural: 'images',
    numberOfImagesToGenerate: 'Number of images to generate',
    generateBetween: 'Generate between 1 and {count} {label}',
    editSuggestion: 'Edit suggestion',
    maxImagesSavedForSuggestion: 'Max images saved for this suggestion',
    savesDefaultMaxForFutureRuns:
      'Saves the default max for future runs of this suggestion.',
    saveDefaults: 'Save defaults',
    aiSuggestions: 'AI Suggestions',
    generatingImageCount: 'Generating {count} image(s)...',
    stopGeneration: 'Stop generation',
    runAll: 'Run all',
    fullAuto: 'Full auto',
    analyzingProductAndGeneratingSuggestions:
      'Analyzing product and generating suggestions...',
    noSuggestionsYet: 'No suggestions yet',
    noSuggestionsDescription:
      'Generate AI-powered suggestions to help you create better assets.',
    generateSuggestions: 'Generate suggestions',
    failedToLoadSuggestions: 'Failed to load suggestions. Please try again.',
    generateImageCountCta: 'Generate {count} {label}',
  },
  walkthrough: {
    productSuggestionsTitle: 'Generate Assets',
    productSuggestionsDescription:
      'One-click generation for different use cases, from product listings and lifestyle shots to social ads and UGC. Pick a type and IMAI generates a batch instantly.',
    productAvatarsTitle: 'Create Or Use Avatars',
    productAvatarsDescription:
      'Build a consistent character workflow for this product. If you do not have avatars yet, jump to Design to create them. If you do, select up to 5 here for lifestyle and social generations.',
    productTabsTitle: 'Choose Your Work Surface',
    productTabsDescription:
      'These tabs do more than organize outputs. They switch the product into different working modes. Media Studio handles visual creation, Catalogue Data handles commerce-ready product info, Prints and Patterns handles repeatable design exploration, and Technical Studio handles production workflows.',
    productGalleryTitle: 'Your Asset Gallery',
    productGalleryDescription:
      'Every generated image, video, and 3D model lives here. Hover over any asset to download it, upscale it, edit it with inpainting, or mention it in chat for further refinement.',
    productAddAssetTitle: 'Add or Generate Assets',
    productAddAssetDescription:
      'Upload your own photos and videos, or generate new ones for this group. Each asset group organizes content by campaign, style, or use case.',
    productChatTabsTitle: 'Chat Workspace',
    productChatTabsDescription:
      'This is a product-specific workspace, not a generic chat. The active thread keeps one line of thinking focused, while Chats lets you move between saved conversations for this product and version.',
    productChatNewTitle: 'Branch Into New Directions',
    productChatNewDescription:
      'Use New chat to start a fresh exploration without overwriting the current conversation. It is the fastest way to compare creative or strategic directions side by side.',
    productChatInputTitle: 'Use Mentions And Context',
    productChatInputDescription:
      'This composer understands the current product, version, and work surface. Mention assets, groups, and references directly in chat so IMAI can work from precise context instead of text alone.',
    productVersionsTitle: 'Version History',
    productVersionsDescription:
      'Each version captures a snapshot of your product. Create new versions to explore different directions without losing previous work.',
    mobileProductTabsTitle: 'Choose Your Work Surface',
    mobileProductTabsDescription:
      'Swipe through these tabs to switch the product into different working modes. Media Studio is for visuals, Catalogue Data is for listings, Prints and Patterns is for repeatable design exploration, and Technical Studio is for production workflows.',
    mobileProductChatTitle: 'Product Workspace Chat',
    mobileProductChatDescription:
      'Tap to open a chat tied to this product and the surface you are viewing. Ask for variations, style changes, or next steps, and IMAI responds with product-aware context.',
  },
};

const VI_COPY: ProductStaticCopy = createVietnameseStaticCopy(EN_COPY);

const ID_COPY: ProductStaticCopy = {
  ...EN_COPY,
  workspace: {
    ...EN_COPY.workspace,
    mediaStudio: 'Studio Media',
    catalogueData: 'Data Katalog',
    printsAndPatterns: 'Cetakan & Pola',
    technicalBlueprints: 'Technical Studio',
    manufacturing: 'Manufaktur',
    threeDModels: 'Model 3D',
    creating: 'Membuat...',
    newChat: 'Obrolan baru',
    chatWithThisProduct: 'Mengobrol tentang produk ini',
    newTab: 'Tab baru',
    chats: 'Obrolan',
    failedToProcessMessage: 'Gagal memproses pesan',
    askAnythingOrMentionAsset: 'Tanya atau @sebut',
    main: 'Utama',
    mainAsset: 'Aset utama',
    generatingImage: 'Membuat gambar',
    productListing: 'Daftar Produk',
    socialMedia: 'Media Sosial',
    lifestyle: 'Gaya Hidup',
    detail: 'Detail',
    layoutView: 'Tampilan tata letak',
    groupedView: 'Tampilan grup',
    allAssetsView: 'Tampilan semua aset',
    listView: 'Tampilan daftar',
    exitSelectionMode: 'Keluar dari mode pilih',
    selecting: 'Memilih',
    selectAssets: 'Pilih aset',
    select: 'Pilih',
    selectedCount: '{count} dipilih',
    clearSelection: 'Hapus pilihan',
    selectAll: 'Pilih semua',
    clear: 'Hapus',
    allCount: 'Semua ({count})',
    downloadSelected: 'Unduh yang dipilih',
    preparing: 'Menyiapkan...',
    download: 'Unduh',
    mentionSelectedInChat: 'Sebutkan yang dipilih di obrolan',
    mention: 'Sebut',
    deleteSelected: 'Hapus yang dipilih',
    delete: 'Hapus',
    editAsset: 'Edit aset',
    viewFullScreen: 'Lihat layar penuh',
    deleteAsset: 'Hapus aset',
    deleteThisAsset: 'Hapus aset ini?',
    selectAsset: 'Pilih Aset',
    openGenerationChat: 'Buka obrolan generasi',
    chooseAssetToDiscuss: 'Pilih aset untuk dibahas di obrolan',
    version: 'Versi',
    noVersionSelected: 'Tidak ada versi yang dipilih',
    noMainAsset: 'Tidak ada aset utama',
    selected: 'Dipilih',
    newLabel: 'Baru',
    moreVersionsAvailable: '{count} versi lainnya tersedia',
    selectedAsset: 'Aset terpilih',
    videoAsset: 'Aset video',
    threeDModel: 'Model 3D',
    replaceAsset: 'Ganti aset',
    maximizeAsset: 'Perbesar aset',
    minimizeAsset: 'Kecilkan aset',
    assetPreview: 'Pratinjau aset',
    imageLabel: 'Gambar',
    openAsset: 'Buka aset',
    selectFileForDownload: 'Pilih file untuk diunduh',
    deselectFile: 'Batalkan pilihan file',
    createdRelative: 'Dibuat {time}',
    updatedRelative: 'Diperbarui {time}',
    generatingVideo: 'Membuat video',
    fineTuningImage: 'Menyempurnakan gambar',
    changeSelectionWhilePinned:
      'Tidak dapat mengubah pilihan saat aset disematkan. Lepas sematan terlebih dahulu.',
    selectVersionBeforePreparingDownload:
      'Pilih versi sebelum menyiapkan unduhan.',
    selectAtLeastOneFile: 'Pilih setidaknya satu file.',
    preparingFilesForDownload: 'Menyiapkan {count} file untuk diunduh',
    addedAssetsToChat: 'Menambahkan {count} aset ke obrolan',
    deleteAssetsTitle: 'Hapus aset?',
    deleteAssetsDescription:
      'Tindakan ini tidak dapat dibatalkan. Aset yang dipilih akan dihapus permanen.',
    deletingAssets: 'Menghapus {count} aset...',
    deletedAssets: '{count} aset dihapus',
    somethingWentWrong: 'Terjadi kesalahan',
    changeLayoutView: 'Ubah tampilan tata letak',
    deleteAssetQuestion: 'Hapus aset ini?',
    deletingAsset: 'Menghapus aset...',
    assetDeleted: 'Aset dihapus',
    generatingAdditionalAssets: 'Membuat aset tambahan...',
    creatingMoreAssets: 'Membuat lebih banyak aset untuk produk Anda',
    generatingMainAsset: 'Membuat aset utama...',

    logoReference: {
      title: 'Referensi Logo',
      description:
        'Simpan referensi logo yang dapat dipakai ulang di sini agar generasi mendatang menjaga tanda, panduan posisi, dan perlakuan warna yang tepat.',
      autoBadge: 'Otomatis',
      manualBadge: 'Manual',
      addManual: 'Unggah logo kamu',
      editManual: 'Unggah logo kamu',
      reextract: 'Jalankan ulang ekstraksi',
      processing: 'Membuat referensi logo.',
      noSavedReference: 'Belum ada referensi logo tersimpan.',
      saveFailed: 'Gagal menyimpan referensi logo.',
      uploadFailed: 'Gagal mengunggah referensi logo.',
      logoTextLabel: 'Teks logo',
      logoTextPlaceholder: 'Wordmark merek atau teks logo',
      contextLabel: 'Konteks',
      contextPlaceholder:
        'Contoh: bordir krem kecil di dada kiri atas pada rajut hitam berusuk',
    },
  },
  avatarSelector: {
    ...EN_COPY.avatarSelector,
    failedToSaveAvatarSelection: 'Gagal menyimpan pilihan avatar',
    failedToRemoveAvatar: 'Gagal menghapus avatar',
    publishedIndividually: 'Dipublikasikan secara terpisah',
    createAvatarCharacterPrompt: 'Buat karakter avatar: {seed}',
    addAvatar: 'Tambah avatar',
    editAvatars: 'Edit avatar',
    createAvatars: 'Buat avatar',
    createAvatarsDescription:
      'Avatar membantu Anda membuat gambar gaya hidup dan sosial dengan karakter yang konsisten.',
    goToDesign: 'Ke Desain',
    useAvatars: 'Gunakan avatar',
    useAvatarsDescription:
      'Pilih hingga 5 avatar untuk memandu pembuatan gambar gaya hidup dan sosial.',
    on: 'Aktif',
    off: 'Nonaktif',
    avatarsEnabledDescription:
      'Avatar yang dipilih akan digunakan untuk gambar gaya hidup dan sosial.',
    avatarsDisabledDescription:
      'Avatar dinonaktifkan. Aktifkan untuk menggunakannya dalam pembuatan.',
    selectAvatars: 'Pilih avatar',
    selectAvatarsDescription:
      'Pilih hingga 5 avatar dari workspace Anda atau set avatar yang dipublikasikan untuk gambar gaya hidup dan sosial.',
    searchAvatars: 'Cari avatar...',
    noAvatarsFound: 'Tidak ada avatar ditemukan',
    tryDifferentSearchTerm: 'Coba kata pencarian lain',
    noAvatarsAvailable: 'Tidak ada avatar tersedia',
    createAvatarsFirst: 'Buat avatar terlebih dahulu di bagian Avatar.',
    yourWorkspace: 'Avatar workspace Anda',
    sharedWithYou: 'Avatar yang dibagikan ke Anda',
    privateAndPublished: 'Pribadi dan dipublikasikan',
    private: 'Avatar pribadi',
    publishedSets: 'Set yang dipublikasikan',
    verifiedPublishers: 'Penerbit terverifikasi',
    avatarCount: '{count} avatar',
    selected: 'Dipilih:',
    cancel: 'Batal',
    save: 'Simpan',
    noChanges: 'Tidak ada perubahan',
    publishedByWorkspace: 'Dipublikasikan oleh {workspaceName}',
    verifiedWorkspace: 'Workspace terverifikasi',
    failed: 'Gagal',
    queued: 'Dalam antrean',
    generating: 'Membuat',
    removeAvatar: 'Hapus {name}',
  },
  generationSuggestions: {
    ...EN_COPY.generationSuggestions,
    generatingAssets: 'Membuat aset',
    starting: 'Memulai...',
    stoppedByUser: 'Dihentikan oleh pengguna',
    regenerateSuggestions: 'Buat ulang saran',
    regenerateSuggestionsDescription:
      'Produk Anda akan dianalisis ulang dan saran baru akan dibuat berdasarkan gambar utama dan galeri saat ini. Saran yang ada akan diganti.',
    regenerate: 'Buat ulang',
    cancel: 'Batal',
    runAllSuggestions: 'Jalankan semua saran',
    runAllSuggestionsDescription:
      'Ini akan menjalankan semua saran ({totalImages} gambar total). Mungkin memerlukan waktu, tetapi aset akan muncul saat selesai.',
    fullAutoMode: 'Mode otomatis penuh',
    fullAutoModeDescription:
      'Sistem akan memutuskan otomatis apa yang harus dibuat berikutnya berdasarkan gambar utama dan galeri saat ini, lalu langsung mulai membuat aset.',
    startFullAutoMode: 'Mulai mode otomatis penuh',
    imageSingular: 'gambar',
    imagePlural: 'gambar',
    numberOfImagesToGenerate: 'Jumlah gambar yang akan dibuat',
    generateBetween: 'Buat antara 1 dan {count} {label}',
    editSuggestion: 'Edit saran',
    maxImagesSavedForSuggestion:
      'Maksimum gambar yang disimpan untuk saran ini',
    savesDefaultMaxForFutureRuns:
      'Menyimpan jumlah maksimum default untuk penggunaan saran ini di masa mendatang.',
    saveDefaults: 'Simpan default',
    aiSuggestions: 'Saran AI',
    generatingImageCount: 'Membuat {count} gambar...',
    stopGeneration: 'Hentikan pembuatan',
    runAll: 'Jalankan semua',
    fullAuto: 'Otomatis penuh',
    analyzingProductAndGeneratingSuggestions:
      'Menganalisis produk dan membuat saran...',
    noSuggestionsYet: 'Belum ada saran',
    noSuggestionsDescription:
      'Buat saran berbasis AI untuk membantu Anda membuat aset yang lebih baik.',
    generateSuggestions: 'Buat saran',
    failedToLoadSuggestions: 'Gagal memuat saran. Silakan coba lagi.',
    generateImageCountCta: 'Buat {count} {label}',
  },
};

const AR_COPY: ProductStaticCopy = {
  workspace: {
    mediaStudio: 'استوديو الوسائط',
    catalogueData: 'بيانات الكتالوج',
    printsAndPatterns: 'المطبوعات والأنماط',
    technicalBlueprints: 'Technical Studio',
    manufacturing: 'التصنيع',
    threeDModels: 'نماذج ثلاثية الأبعاد',
    creating: 'جارٍ الإنشاء...',
    newChat: 'محادثة جديدة',
    chatWithThisProduct: 'تحدث مع هذا المنتج',
    newTab: 'تبويب جديد',
    chats: 'المحادثات',
    failedToProcessMessage: 'تعذر معالجة الرسالة',
    askAnythingOrMentionAsset: 'اسأل أو استخدم @',
    main: 'الرئيسي',
    mainAsset: 'الأصل الرئيسي',
    generatingImage: 'جارٍ إنشاء الصورة',
    productListing: 'عرض المنتج',
    socialMedia: 'وسائل التواصل الاجتماعي',
    lifestyle: 'أسلوب الحياة',
    detail: 'التفاصيل',
    layoutView: 'عرض التخطيط',
    groupedView: 'العرض المجمّع',
    allAssetsView: 'عرض كل الأصول',
    listView: 'عرض القائمة',
    exitSelectionMode: 'إنهاء وضع التحديد',
    selecting: 'جارٍ التحديد',
    selectAssets: 'تحديد الأصول',
    select: 'تحديد',
    selectedCount: 'تم تحديد {count}',
    clearSelection: 'مسح التحديد',
    selectAll: 'تحديد الكل',
    clear: 'مسح',
    allCount: 'الكل ({count})',
    downloadSelected: 'تنزيل المحدد',
    preparing: 'جارٍ التحضير...',
    download: 'تنزيل',
    mentionSelectedInChat: 'ذكر المحدد في المحادثة',
    mention: 'ذكر',
    deleteSelected: 'حذف المحدد',
    delete: 'حذف',
    editAsset: 'تعديل الأصل',
    viewFullScreen: 'عرض بملء الشاشة',
    deleteAsset: 'حذف الأصل',
    deleteThisAsset: 'هل تريد حذف هذا الأصل؟',
    selectAsset: 'تحديد أصل',
    openGenerationChat: 'فتح دردشة الإنشاء',
    chooseAssetToDiscuss: 'اختر أصلاً لمناقشته في المحادثة',
    mainAssetAlt: 'الأصل الرئيسي لـ {productName}',
    version: 'الإصدار',
    noVersionSelected: 'لم يتم اختيار إصدار',
    noMainAsset: 'لا يوجد أصل رئيسي',
    selected: 'محدد',
    newLabel: 'جديد',
    moreVersionsAvailable: '{count} إصدار إضافي متاح',
    selectedAsset: 'الأصل المحدد',
    videoAsset: 'أصل فيديو',
    threeDModel: 'نموذج ثلاثي الأبعاد',
    replaceAsset: 'استبدال الأصل',
    maximizeAsset: 'تكبير الأصل',
    minimizeAsset: 'تصغير الأصل',
    assetPreview: 'معاينة الأصل',
    imageLabel: 'صورة',
    openAsset: 'فتح الأصل',
    selectFileForDownload: 'اختر الملف للتنزيل',
    deselectFile: 'إلغاء تحديد الملف',
    createdRelative: 'تم الإنشاء {time}',
    updatedRelative: 'تم التحديث {time}',
    generatingVideo: 'جارٍ إنشاء الفيديو',
    fineTuningImage: 'جارٍ تحسين الصورة',
    changeSelectionWhilePinned:
      'لا يمكن تغيير التحديد أثناء تثبيت الأصل. قم بإلغاء التثبيت أولاً.',
    selectVersionBeforePreparingDownload: 'اختر إصداراً قبل تجهيز التنزيل.',
    selectAtLeastOneFile: 'اختر ملفاً واحداً على الأقل.',
    preparingFilesForDownload: 'جارٍ تجهيز {count} ملف للتنزيل',
    addedAssetsToChat: 'تمت إضافة {count} أصل إلى المحادثة',
    deleteAssetsTitle: 'حذف الأصول؟',
    deleteAssetsDescription:
      'لا يمكن التراجع عن هذا الإجراء. سيتم حذف الأصول المحددة نهائياً.',
    deletingAssets: 'جارٍ حذف {count} أصل...',
    deletedAssets: 'تم حذف {count} أصل',
    somethingWentWrong: 'حدث خطأ ما',
    changeLayoutView: 'تغيير عرض التخطيط',
    deleteAssetQuestion: 'حذف هذا الأصل؟',
    deletingAsset: 'جارٍ حذف الأصل...',
    assetDeleted: 'تم حذف الأصل',
    generatingAdditionalAssets: 'جارٍ إنشاء أصول إضافية...',
    creatingMoreAssets: 'جارٍ إنشاء المزيد من الأصول لمنتجك',
    generatingMainAsset: 'جارٍ إنشاء الأصل الرئيسي...',

    logoReference: {
      title: 'مرجع الشعار',
      description:
        'احتفظ بمرجع شعار قابل لإعادة الاستخدام هنا حتى تحافظ الأجيال القادمة على العلامة الصحيحة وتلميحات الموضع ومعالجة الألوان.',
      autoBadge: 'تلقائي',
      manualBadge: 'يدوي',
      addManual: 'ارفع شعارك',
      editManual: 'ارفع شعارك',
      reextract: 'إعادة تشغيل الاستخراج',
      processing: 'جارٍ إنشاء مرجع الشعار.',
      noSavedReference: 'لا يوجد مرجع شعار محفوظ بعد.',
      saveFailed: 'فشل حفظ مرجع الشعار.',
      uploadFailed: 'فشل رفع مرجع الشعار.',
      logoTextLabel: 'نص الشعار',
      logoTextPlaceholder: 'علامة العلامة التجارية أو نص الشعار',
      contextLabel: 'السياق',
      contextPlaceholder:
        'مثال: تطريز كريمي صغير على الصدر الأيسر العلوي فوق تريكو أسود مضلع',
    },
  },
  avatarSelector: {
    failedToSaveAvatarSelection: 'تعذر حفظ اختيار الصورة الرمزية',
    failedToRemoveAvatar: 'تعذر إزالة الصورة الرمزية',
    publishedIndividually: 'منشور بشكل فردي',
    createAvatarCharacterPrompt: 'أنشئ شخصية صورة رمزية: {seed}',
    addAvatar: 'إضافة صورة رمزية',
    editAvatars: 'تعديل الصور الرمزية',
    createAvatars: 'إنشاء صور رمزية',
    createAvatarsDescription:
      'تساعدك الصور الرمزية على إنشاء لقطات أسلوب حياة واجتماعية بشخصية متسقة.',
    goToDesign: 'الانتقال إلى التصميم',
    useAvatars: 'استخدام الصور الرمزية',
    useAvatarsDescription:
      'اختر حتى 5 صور رمزية لتوجيه إنشاء صور أسلوب الحياة والصور الاجتماعية.',
    on: 'تشغيل',
    off: 'إيقاف',
    avatarsEnabledDescription:
      'سيتم استخدام الصور الرمزية المحددة في لقطات أسلوب الحياة والاجتماعية.',
    avatarsDisabledDescription:
      'الصور الرمزية معطلة. فعّلها لاستخدامها في الإنشاء.',
    selectAvatars: 'اختيار الصور الرمزية',
    selectAvatarsDescription:
      'اختر حتى 5 صور رمزية من مساحة عملك أو من المجموعات المنشورة لأسلوب الحياة واللقطات الاجتماعية.',
    searchAvatars: 'ابحث عن الصور الرمزية...',
    noAvatarsFound: 'لم يتم العثور على صور رمزية',
    tryDifferentSearchTerm: 'جرّب كلمة بحث مختلفة',
    noAvatarsAvailable: 'لا توجد صور رمزية متاحة',
    createAvatarsFirst: 'أنشئ صوراً رمزية في قسم الصور الرمزية أولاً.',
    yourWorkspace: 'أفاتارات مساحة عملك',
    sharedWithYou: 'أفاتارات مشتركة معك',
    privateAndPublished: 'خاص ومنشور',
    private: 'أفاتارات خاصة',
    publishedSets: 'المجموعات المنشورة',
    verifiedPublishers: 'ناشرون موثوقون',
    avatarCount: '{count} صور رمزية',
    selected: 'المحدد:',
    cancel: 'إلغاء',
    save: 'حفظ',
    noChanges: 'لا توجد تغييرات',
    publishedByWorkspace: 'منشور بواسطة {workspaceName}',
    verifiedWorkspace: 'مساحة عمل موثقة',
    failed: 'فشل',
    queued: 'في قائمة الانتظار',
    generating: 'جارٍ الإنشاء',
    removeAvatar: 'إزالة {name}',
    avatarSeedPrompts: AVATAR_SEED_PROMPTS,
  },
  generationSuggestions: {
    generatingAssets: 'جارٍ إنشاء الأصول',
    starting: 'جارٍ البدء...',
    stoppedByUser: 'أوقفه المستخدم',
    regenerateSuggestions: 'إعادة إنشاء الاقتراحات',
    regenerateSuggestionsDescription:
      'سيُعاد تحليل منتجك وإنشاء اقتراحات جديدة بناءً على الصورة الرئيسية الحالية والمعرض. سيتم استبدال اقتراحاتك الحالية.',
    regenerate: 'إعادة الإنشاء',
    cancel: 'إلغاء',
    runAllSuggestions: 'تشغيل كل الاقتراحات',
    runAllSuggestionsDescription:
      'سيتم تشغيل جميع الاقتراحات (إجمالي {totalImages} صورة). قد يستغرق ذلك بعض الوقت، وستظهر الأصول تباعاً عند اكتمالها.',
    fullAutoMode: 'الوضع التلقائي الكامل',
    fullAutoModeDescription:
      'سيقرر النظام تلقائياً ما يجب إنشاؤه بعد ذلك بناءً على الصورة الرئيسية الحالية والمعرض، ثم يبدأ فوراً في إنشاء الأصول.',
    startFullAutoMode: 'بدء الوضع التلقائي الكامل',
    imageSingular: 'صورة',
    imagePlural: 'صور',
    numberOfImagesToGenerate: 'عدد الصور المطلوب إنشاؤها',
    generateBetween: 'أنشئ بين 1 و {count} {label}',
    editSuggestion: 'تعديل الاقتراح',
    maxImagesSavedForSuggestion: 'الحد الأقصى للصور المحفوظة لهذا الاقتراح',
    savesDefaultMaxForFutureRuns:
      'يحفظ الحد الأقصى الافتراضي للتشغيلات المستقبلية لهذا الاقتراح.',
    saveDefaults: 'حفظ الإعدادات الافتراضية',
    aiSuggestions: 'اقتراحات الذكاء الاصطناعي',
    generatingImageCount: 'جارٍ إنشاء {count} صورة...',
    stopGeneration: 'إيقاف الإنشاء',
    runAll: 'تشغيل الكل',
    fullAuto: 'تلقائي كامل',
    analyzingProductAndGeneratingSuggestions:
      'جارٍ تحليل المنتج وإنشاء الاقتراحات...',
    noSuggestionsYet: 'لا توجد اقتراحات بعد',
    noSuggestionsDescription:
      'أنشئ اقتراحات مدعومة بالذكاء الاصطناعي لمساعدتك على إنشاء أصول أفضل.',
    generateSuggestions: 'إنشاء اقتراحات',
    failedToLoadSuggestions: 'تعذر تحميل الاقتراحات. يرجى المحاولة مرة أخرى.',
    generateImageCountCta: 'إنشاء {count} {label}',
  },
  walkthrough: {
    productSuggestionsTitle: 'إنشاء الأصول',
    productSuggestionsDescription:
      'إنشاء بنقرة واحدة لاستخدامات متعددة، من صور القوائم ولقطات أسلوب الحياة إلى الإعلانات الاجتماعية ومحتوى UGC. اختر النوع وسيولّد IMAI دفعة كاملة فوراً.',
    productAvatarsTitle: 'أنشئ الصور الرمزية أو استخدمها',
    productAvatarsDescription:
      'ابنِ سير عمل شخصيات متسقاً لهذا المنتج. إذا لم تكن لديك صور رمزية بعد، انتقل إلى التصميم لإنشائها. وإذا كانت لديك، اختر حتى 5 هنا لاستخدامها في لقطات أسلوب الحياة والاجتماعية.',
    productTabsTitle: 'اختر سطح العمل',
    productTabsDescription:
      'هذه التبويبات لا تنظّم المخرجات فقط، بل تبدّل المنتج بين أوضاع عمل مختلفة. استوديو الوسائط للإنشاء البصري، وبيانات الكتالوج لمعلومات المنتج الجاهزة للتجارة، والمطبوعات والأنماط للاستكشاف التصميمي المتكرر، والمخططات التقنية لسير عمل الإنتاج.',
    productGalleryTitle: 'معرض الأصول',
    productGalleryDescription:
      'كل صورة وفيديو ونموذج ثلاثي الأبعاد يتم إنشاؤه يعيش هنا. مرّر فوق أي أصل لتنزيله أو تحسينه أو تعديله بالطلاء الداخلي أو ذكره في المحادثة لمزيد من التنقيح.',
    productAddAssetTitle: 'أضف أصولاً أو أنشئها',
    productAddAssetDescription:
      'حمّل صورك وفيديوهاتك الخاصة أو أنشئ أصولاً جديدة لهذه المجموعة. تنظّم كل مجموعة أصول المحتوى حسب الحملة أو الأسلوب أو حالة الاستخدام.',
    productChatTabsTitle: 'مساحة عمل المحادثة',
    productChatTabsDescription:
      'هذه مساحة عمل خاصة بالمنتج وليست محادثة عامة. يحافظ الخيط النشط على تركيز فكرة واحدة، بينما تتيح لك المحادثات التنقل بين المحادثات المحفوظة لهذا المنتج والإصدار.',
    productChatNewTitle: 'تفرّع إلى اتجاهات جديدة',
    productChatNewDescription:
      'استخدم محادثة جديدة لبدء استكشاف جديد دون الكتابة فوق المحادثة الحالية. إنها أسرع طريقة لمقارنة الاتجاهات الإبداعية أو الاستراتيجية جنباً إلى جنب.',
    productChatInputTitle: 'استخدم الإشارات والسياق',
    productChatInputDescription:
      'يفهم هذا المحرر المنتج الحالي والإصدار وسطح العمل. يمكنك ذكر الأصول والمجموعات والمراجع مباشرة في المحادثة ليعمل IMAI من سياق دقيق بدلاً من النص وحده.',
    productVersionsTitle: 'سجل الإصدارات',
    productVersionsDescription:
      'يلتقط كل إصدار لقطة من منتجك. أنشئ إصدارات جديدة لاستكشاف اتجاهات مختلفة دون فقدان العمل السابق.',
    mobileProductTabsTitle: 'اختر سطح العمل',
    mobileProductTabsDescription:
      'مرّر بين هذه التبويبات للتبديل بين أوضاع العمل المختلفة للمنتج. استوديو الوسائط للمرئيات، وبيانات الكتالوج للقوائم، والمطبوعات والأنماط للاستكشاف التصميمي المتكرر، والمخططات التقنية لسير عمل الإنتاج.',
    mobileProductChatTitle: 'محادثة مساحة عمل المنتج',
    mobileProductChatDescription:
      'اضغط لفتح محادثة مرتبطة بهذا المنتج والسطح الذي تشاهده. اطلب تنويعات أو تغييرات أسلوب أو خطوات تالية، وسيستجيب IMAI بسياق واعٍ بالمنتج.',
  },
};

const DE_COPY: ProductStaticCopy = {
  workspace: {
    mediaStudio: 'Media Studio',
    catalogueData: 'Katalogdaten',
    printsAndPatterns: 'Drucke und Muster',
    technicalBlueprints: 'Technical Studio',
    manufacturing: 'Fertigung',
    threeDModels: '3D-Modelle',
    creating: 'Wird erstellt...',
    newChat: 'Neuer Chat',
    chatWithThisProduct: 'Mit diesem Produkt chatten',
    newTab: 'Neuer Tab',
    chats: 'Chats',
    failedToProcessMessage: 'Nachricht konnte nicht verarbeitet werden',
    askAnythingOrMentionAsset: 'Frag oder nutze @',
    main: 'Haupt',
    mainAsset: 'Haupt-Asset',
    generatingImage: 'Bild wird erstellt',
    productListing: 'Produktlisting',
    socialMedia: 'Social Media',
    lifestyle: 'Lifestyle',
    detail: 'Detail',
    layoutView: 'Layoutansicht',
    groupedView: 'Gruppenansicht',
    allAssetsView: 'Alle Assets',
    listView: 'Listenansicht',
    exitSelectionMode: 'Auswahlmodus beenden',
    selecting: 'Auswählen',
    selectAssets: 'Assets auswählen',
    select: 'Auswählen',
    selectedCount: '{count} ausgewählt',
    clearSelection: 'Auswahl aufheben',
    selectAll: 'Alle auswählen',
    clear: 'Leeren',
    allCount: 'Alle ({count})',
    downloadSelected: 'Ausgewählte herunterladen',
    preparing: 'Wird vorbereitet...',
    download: 'Herunterladen',
    mentionSelectedInChat: 'Ausgewählte im Chat erwähnen',
    mention: 'Erwähnen',
    deleteSelected: 'Ausgewählte löschen',
    delete: 'Löschen',
    editAsset: 'Asset bearbeiten',
    viewFullScreen: 'Vollbild anzeigen',
    deleteAsset: 'Asset löschen',
    deleteThisAsset: 'Dieses Asset löschen?',
    selectAsset: 'Asset auswählen',
    openGenerationChat: 'Generierungs-Chat öffnen',
    chooseAssetToDiscuss:
      'Wähle ein Asset aus, das du im Chat besprechen möchtest',
    mainAssetAlt: 'Haupt-Asset von {productName}',
    version: 'Version',
    noVersionSelected: 'Keine Version ausgewählt',
    noMainAsset: 'Kein Haupt-Asset',
    selected: 'Ausgewählt',
    newLabel: 'Neu',
    moreVersionsAvailable: 'Noch {count} Versionen verfügbar',
    selectedAsset: 'Ausgewähltes Asset',
    videoAsset: 'Video-Asset',
    threeDModel: '3D-Modell',
    replaceAsset: 'Asset ersetzen',
    maximizeAsset: 'Asset maximieren',
    minimizeAsset: 'Asset minimieren',
    assetPreview: 'Asset-Vorschau',
    imageLabel: 'Bild',
    openAsset: 'Asset öffnen',
    selectFileForDownload: 'Datei zum Download auswählen',
    deselectFile: 'Dateiauswahl aufheben',
    createdRelative: 'Erstellt {time}',
    updatedRelative: 'Aktualisiert {time}',
    generatingVideo: 'Video wird erstellt',
    fineTuningImage: 'Bild wird feinabgestimmt',
    changeSelectionWhilePinned:
      'Die Auswahl kann nicht geändert werden, solange das Asset fixiert ist. Bitte zuerst lösen.',
    selectVersionBeforePreparingDownload:
      'Wähle eine Version, bevor du einen Download vorbereitest.',
    selectAtLeastOneFile: 'Wähle mindestens eine Datei aus.',
    preparingFilesForDownload:
      '{count} Dateien werden für den Download vorbereitet',
    addedAssetsToChat: '{count} Asset(s) zum Chat hinzugefügt',
    deleteAssetsTitle: 'Assets löschen?',
    deleteAssetsDescription:
      'Diese Aktion kann nicht rückgängig gemacht werden. Die ausgewählten Assets werden dauerhaft gelöscht.',
    deletingAssets: '{count} Asset(s) werden gelöscht...',
    deletedAssets: '{count} Asset(s) gelöscht',
    somethingWentWrong: 'Etwas ist schiefgelaufen',
    changeLayoutView: 'Layoutansicht ändern',
    deleteAssetQuestion: 'Dieses Asset löschen?',
    deletingAsset: 'Asset wird gelöscht...',
    assetDeleted: 'Asset gelöscht',
    generatingAdditionalAssets: 'Zusätzliche Assets werden erstellt...',
    creatingMoreAssets: 'Es werden weitere Assets für dein Produkt erstellt',
    generatingMainAsset: 'Haupt-Asset wird erstellt...',

    logoReference: {
      title: 'Logo-Referenz',
      description:
        'Hinterlege hier eine wiederverwendbare Logo-Referenz, damit zukünftige Generierungen das richtige Markenzeichen, Platzierungshinweise und Farbbehandlung beibehalten.',
      autoBadge: 'Auto',
      manualBadge: 'Manuell',
      addManual: 'Lade dein Logo hoch',
      editManual: 'Lade dein Logo hoch',
      reextract: 'Extraktion erneut ausführen',
      processing: 'Logo-Referenz wird erstellt.',
      noSavedReference: 'Noch keine Logo-Referenz gespeichert.',
      saveFailed: 'Speichern der Logo-Referenz fehlgeschlagen.',
      uploadFailed: 'Hochladen der Logo-Referenz fehlgeschlagen.',
      logoTextLabel: 'Logo-Text',
      logoTextPlaceholder: 'Wortmarke oder Logo-Text',
      contextLabel: 'Kontext',
      contextPlaceholder:
        'Beispiel: kleine, tonale cremefarbene Stickerei oben links auf der Brust über geripptem schwarzem Strick',
    },
  },
  avatarSelector: {
    failedToSaveAvatarSelection:
      'Avatar-Auswahl konnte nicht gespeichert werden',
    failedToRemoveAvatar: 'Avatar konnte nicht entfernt werden',
    publishedIndividually: 'Einzeln veröffentlicht',
    createAvatarCharacterPrompt: 'Erstelle einen Avatar-Charakter: {seed}',
    addAvatar: 'Avatar hinzufügen',
    editAvatars: 'Avatare bearbeiten',
    createAvatars: 'Avatare erstellen',
    createAvatarsDescription:
      'Avatare helfen dir, Lifestyle- und Social-Motive mit einer konsistenten Figur zu erzeugen.',
    goToDesign: 'Zu Design',
    useAvatars: 'Avatare verwenden',
    useAvatarsDescription:
      'Wähle bis zu 5 Avatare aus, um Lifestyle- und Social-Bilder zu steuern.',
    on: 'An',
    off: 'Aus',
    avatarsEnabledDescription:
      'Ausgewählte Avatare werden für Lifestyle- und Social-Motive verwendet.',
    avatarsDisabledDescription:
      'Avatare sind deaktiviert. Schalte sie ein, um sie für Generierungen zu nutzen.',
    selectAvatars: 'Avatare auswählen',
    selectAvatarsDescription:
      'Wähle bis zu 5 Avatare aus deinem Workspace oder veröffentlichten Sets für Lifestyle- und Social-Motive.',
    searchAvatars: 'Avatare suchen...',
    noAvatarsFound: 'Keine Avatare gefunden',
    tryDifferentSearchTerm: 'Versuche einen anderen Suchbegriff',
    noAvatarsAvailable: 'Keine Avatare verfügbar',
    createAvatarsFirst: 'Erstelle zuerst Avatare im Avatar-Bereich.',
    yourWorkspace: 'Deine Workspace-Avatare',
    sharedWithYou: 'Geteilte Avatare',
    privateAndPublished: 'Privat und veröffentlicht',
    private: 'Private Avatare',
    publishedSets: 'Veröffentlichte Sets',
    verifiedPublishers: 'Verifizierte Publisher',
    avatarCount: '{count} Avatare',
    selected: 'Ausgewählt:',
    cancel: 'Abbrechen',
    save: 'Speichern',
    noChanges: 'Keine Änderungen',
    publishedByWorkspace: 'Veröffentlicht von {workspaceName}',
    verifiedWorkspace: 'Verifizierter Workspace',
    failed: 'Fehlgeschlagen',
    queued: 'In Warteschlange',
    generating: 'Wird erstellt',
    removeAvatar: '{name} entfernen',
    avatarSeedPrompts: AVATAR_SEED_PROMPTS,
  },
  generationSuggestions: {
    generatingAssets: 'Assets werden erstellt',
    starting: 'Startet...',
    stoppedByUser: 'Vom Nutzer gestoppt',
    regenerateSuggestions: 'Vorschläge neu erstellen',
    regenerateSuggestionsDescription:
      'Dadurch wird dein Produkt erneut analysiert und neue Vorschläge auf Basis des aktuellen Hauptbilds und der Galerie erstellt. Bestehende Vorschläge werden ersetzt.',
    regenerate: 'Neu erstellen',
    cancel: 'Abbrechen',
    runAllSuggestions: 'Alle Vorschläge ausführen',
    runAllSuggestionsDescription:
      'Dadurch werden alle Vorschläge ausgeführt ({totalImages} Bilder insgesamt). Das kann etwas dauern, aber Assets erscheinen nach und nach.',
    fullAutoMode: 'Vollautomatischer Modus',
    fullAutoModeDescription:
      'Das System entscheidet automatisch, was als Nächstes generiert wird, basierend auf Hauptbild und vorhandener Galerie, und startet sofort mit der Generierung.',
    startFullAutoMode: 'Vollautomatik starten',
    imageSingular: 'Bild',
    imagePlural: 'Bilder',
    numberOfImagesToGenerate: 'Anzahl der zu generierenden Bilder',
    generateBetween: 'Erzeuge zwischen 1 und {count} {label}',
    editSuggestion: 'Vorschlag bearbeiten',
    maxImagesSavedForSuggestion:
      'Maximale Bildanzahl, die für diesen Vorschlag gespeichert wird',
    savesDefaultMaxForFutureRuns:
      'Speichert den Standard-Höchstwert für zukünftige Durchläufe dieses Vorschlags.',
    saveDefaults: 'Standards speichern',
    aiSuggestions: 'KI-Vorschläge',
    generatingImageCount: '{count} Bild(er) werden erstellt...',
    stopGeneration: 'Generierung stoppen',
    runAll: 'Alle ausführen',
    fullAuto: 'Vollautomatik',
    analyzingProductAndGeneratingSuggestions:
      'Produkt wird analysiert und Vorschläge werden erstellt...',
    noSuggestionsYet: 'Noch keine Vorschläge',
    noSuggestionsDescription:
      'Erzeuge KI-gestützte Vorschläge, um bessere Assets zu erstellen.',
    generateSuggestions: 'Vorschläge erzeugen',
    failedToLoadSuggestions:
      'Vorschläge konnten nicht geladen werden. Bitte versuche es erneut.',
    generateImageCountCta: '{count} {label} erzeugen',
  },
  walkthrough: {
    productSuggestionsTitle: 'Assets generieren',
    productSuggestionsDescription:
      'Erzeuge mit einem Klick Assets für verschiedene Anwendungsfälle, von Produktlistings und Lifestyle-Motiven bis zu Social Ads und UGC. Wähle einen Typ und IMAI erstellt sofort einen Stapel.',
    productAvatarsTitle: 'Avatare erstellen oder nutzen',
    productAvatarsDescription:
      'Baue einen konsistenten Charakter-Workflow für dieses Produkt auf. Wenn du noch keine Avatare hast, springe zu Design und erstelle sie dort. Wenn du welche hast, wähle hier bis zu 5 für Lifestyle- und Social-Generierungen aus.',
    productTabsTitle: 'Arbeitsfläche wählen',
    productTabsDescription:
      'Diese Tabs organisieren nicht nur Ergebnisse, sie schalten das Produkt in verschiedene Arbeitsmodi um. Media Studio ist für visuelle Erstellung, Katalogdaten für commerce-taugliche Produktinfos, Drucke und Muster für wiederholbare Design-Erkundung und Technische Blaupausen für Produktions-Workflows.',
    productGalleryTitle: 'Deine Asset-Galerie',
    productGalleryDescription:
      'Alle generierten Bilder, Videos und 3D-Modelle leben hier. Fahre über ein Asset, um es herunterzuladen, hochzuskalieren, mit Inpainting zu bearbeiten oder es im Chat zur weiteren Verfeinerung zu erwähnen.',
    productAddAssetTitle: 'Assets hinzufügen oder generieren',
    productAddAssetDescription:
      'Lade eigene Fotos und Videos hoch oder generiere neue für diese Gruppe. Jede Asset-Gruppe organisiert Inhalte nach Kampagne, Stil oder Anwendungsfall.',
    productChatTabsTitle: 'Chat-Arbeitsbereich',
    productChatTabsDescription:
      'Dies ist ein produktspezifischer Arbeitsbereich und kein allgemeiner Chat. Der aktive Thread hält eine Denkspur fokussiert, während Chats dich zwischen gespeicherten Gesprächen für dieses Produkt und diese Version wechseln lässt.',
    productChatNewTitle: 'Neue Richtungen einschlagen',
    productChatNewDescription:
      'Nutze Neuer Chat, um eine frische Erkundung zu starten, ohne die aktuelle Unterhaltung zu überschreiben. Das ist der schnellste Weg, kreative oder strategische Richtungen nebeneinander zu vergleichen.',
    productChatInputTitle: 'Erwähnungen und Kontext nutzen',
    productChatInputDescription:
      'Dieser Composer versteht das aktuelle Produkt, die Version und die Arbeitsfläche. Erwähne Assets, Gruppen und Referenzen direkt im Chat, damit IMAI mit präzisem Kontext statt nur mit Text arbeiten kann.',
    productVersionsTitle: 'Versionsverlauf',
    productVersionsDescription:
      'Jede Version hält eine Momentaufnahme deines Produkts fest. Erstelle neue Versionen, um verschiedene Richtungen zu erkunden, ohne frühere Arbeit zu verlieren.',
    mobileProductTabsTitle: 'Arbeitsfläche wählen',
    mobileProductTabsDescription:
      'Wische durch diese Tabs, um das Produkt in unterschiedliche Arbeitsmodi zu versetzen. Media Studio ist für Visuals, Katalogdaten für Listings, Drucke und Muster für wiederholbare Design-Erkundung und Technische Blaupausen für Produktions-Workflows.',
    mobileProductChatTitle: 'Produkt-Workspace-Chat',
    mobileProductChatDescription:
      'Tippe hier, um einen Chat zu öffnen, der mit diesem Produkt und der aktuellen Oberfläche verknüpft bleibt. Bitte um Varianten, Stiländerungen oder nächste Schritte und IMAI antwortet mit produktbezogenem Kontext.',
  },
};

const ES_COPY: ProductStaticCopy = {
  workspace: {
    mediaStudio: 'Media Studio',
    catalogueData: 'Datos del catálogo',
    printsAndPatterns: 'Estampados y patrones',
    technicalBlueprints: 'Technical Studio',
    manufacturing: 'Fabricación',
    threeDModels: 'Modelos 3D',
    creating: 'Creando...',
    newChat: 'Nuevo chat',
    chatWithThisProduct: 'Chatear sobre este producto',
    newTab: 'Nueva pestaña',
    chats: 'Chats',
    failedToProcessMessage: 'No se pudo procesar el mensaje',
    askAnythingOrMentionAsset: 'Pregunta o usa @',
    main: 'Principal',
    mainAsset: 'Recurso principal',
    generatingImage: 'Generando imagen',
    productListing: 'Listado del producto',
    socialMedia: 'Redes sociales',
    lifestyle: 'Estilo de vida',
    detail: 'Detalle',
    layoutView: 'Vista de diseño',
    groupedView: 'Vista agrupada',
    allAssetsView: 'Todos los recursos',
    listView: 'Vista de lista',
    exitSelectionMode: 'Salir del modo de selección',
    selecting: 'Seleccionando',
    selectAssets: 'Seleccionar recursos',
    select: 'Seleccionar',
    selectedCount: '{count} seleccionados',
    clearSelection: 'Borrar selección',
    selectAll: 'Seleccionar todo',
    clear: 'Borrar',
    allCount: 'Todos ({count})',
    downloadSelected: 'Descargar seleccionados',
    preparing: 'Preparando...',
    download: 'Descargar',
    mentionSelectedInChat: 'Mencionar la selección en el chat',
    mention: 'Mencionar',
    deleteSelected: 'Eliminar seleccionados',
    delete: 'Eliminar',
    editAsset: 'Editar recurso',
    viewFullScreen: 'Ver en pantalla completa',
    deleteAsset: 'Eliminar recurso',
    deleteThisAsset: '¿Eliminar este recurso?',
    selectAsset: 'Seleccionar recurso',
    openGenerationChat: 'Abrir chat de generación',
    chooseAssetToDiscuss: 'Elige un recurso para comentarlo en el chat',
    mainAssetAlt: 'recurso principal de {productName}',
    version: 'Versión',
    noVersionSelected: 'No hay una versión seleccionada',
    noMainAsset: 'No hay un recurso principal',
    selected: 'Seleccionado',
    newLabel: 'Nuevo',
    moreVersionsAvailable: 'Hay {count} versiones más disponibles',
    selectedAsset: 'Recurso seleccionado',
    videoAsset: 'Recurso de video',
    threeDModel: 'Modelo 3D',
    replaceAsset: 'Reemplazar recurso',
    maximizeAsset: 'Maximizar recurso',
    minimizeAsset: 'Minimizar recurso',
    assetPreview: 'Vista previa del recurso',
    imageLabel: 'Imagen',
    openAsset: 'Abrir recurso',
    selectFileForDownload: 'Seleccionar archivo para descargar',
    deselectFile: 'Deseleccionar archivo',
    createdRelative: 'Creado {time}',
    updatedRelative: 'Actualizado {time}',
    generatingVideo: 'Generando video',
    fineTuningImage: 'Ajustando la imagen',
    changeSelectionWhilePinned:
      'No se puede cambiar la selección mientras el recurso está fijado. Desfíjalo primero.',
    selectVersionBeforePreparingDownload:
      'Selecciona una versión antes de preparar la descarga.',
    selectAtLeastOneFile: 'Selecciona al menos un archivo.',
    preparingFilesForDownload: 'Preparando {count} archivo(s) para descargar',
    addedAssetsToChat: 'Se añadieron {count} recurso(s) al chat',
    deleteAssetsTitle: '¿Eliminar recursos?',
    deleteAssetsDescription:
      'Esta acción no se puede deshacer. Los recursos seleccionados se eliminarán permanentemente.',
    deletingAssets: 'Eliminando {count} recurso(s)...',
    deletedAssets: 'Se eliminaron {count} recurso(s)',
    somethingWentWrong: 'Algo salió mal',
    changeLayoutView: 'Cambiar vista del diseño',
    deleteAssetQuestion: '¿Eliminar este recurso?',
    deletingAsset: 'Eliminando recurso...',
    assetDeleted: 'Recurso eliminado',
    generatingAdditionalAssets: 'Generando recursos adicionales...',
    creatingMoreAssets: 'Creando más recursos para tu producto',
    generatingMainAsset: 'Generando recurso principal...',

    logoReference: {
      title: 'Referencia de logo',
      description:
        'Guarda aquí una referencia de logo reutilizable para que las generaciones futuras conserven la marca, las pistas de colocación y el tratamiento de color correctos.',
      autoBadge: 'Auto',
      manualBadge: 'Manual',
      addManual: 'Sube tu logo',
      editManual: 'Sube tu logo',
      reextract: 'Volver a ejecutar extracción',
      processing: 'Generando referencia del logo.',
      noSavedReference: 'Aún no hay referencia de logo guardada.',
      saveFailed: 'Error al guardar la referencia del logo.',
      uploadFailed: 'Error al subir la referencia del logo.',
      logoTextLabel: 'Texto del logo',
      logoTextPlaceholder: 'Wordmark de marca o texto del logo',
      contextLabel: 'Contexto',
      contextPlaceholder:
        'Ejemplo: pequeño bordado tonal crema en el pecho superior izquierdo sobre punto negro acanalado',
    },
  },
  avatarSelector: {
    failedToSaveAvatarSelection: 'No se pudo guardar la selección de avatares',
    failedToRemoveAvatar: 'No se pudo quitar el avatar',
    publishedIndividually: 'Publicado de forma individual',
    createAvatarCharacterPrompt: 'Crea un personaje avatar: {seed}',
    addAvatar: 'Añadir avatar',
    editAvatars: 'Editar avatares',
    createAvatars: 'Crear avatares',
    createAvatarsDescription:
      'Los avatares te ayudan a generar escenas sociales y de estilo de vida con un personaje coherente.',
    goToDesign: 'Ir a Diseño',
    useAvatars: 'Usar avatares',
    useAvatarsDescription:
      'Elige hasta 5 avatares para guiar las generaciones sociales y de estilo de vida.',
    on: 'Activado',
    off: 'Desactivado',
    avatarsEnabledDescription:
      'Los avatares seleccionados se usarán en escenas sociales y de estilo de vida.',
    avatarsDisabledDescription:
      'Los avatares están desactivados. Actívalos para usarlos en las generaciones.',
    selectAvatars: 'Seleccionar avatares',
    selectAvatarsDescription:
      'Elige hasta 5 avatares de tu espacio de trabajo o de conjuntos publicados para escenas sociales y de estilo de vida.',
    searchAvatars: 'Buscar avatares...',
    noAvatarsFound: 'No se encontraron avatares',
    tryDifferentSearchTerm: 'Prueba con otro término de búsqueda',
    noAvatarsAvailable: 'No hay avatares disponibles',
    createAvatarsFirst: 'Primero crea avatares en la sección de Avatares.',
    yourWorkspace: 'Avatares de tu espacio de trabajo',
    sharedWithYou: 'Avatares compartidos contigo',
    privateAndPublished: 'Privados y publicados',
    private: 'Avatares privados',
    publishedSets: 'Conjuntos publicados',
    verifiedPublishers: 'Publicadores verificados',
    avatarCount: '{count} avatares',
    selected: 'Seleccionados:',
    cancel: 'Cancelar',
    save: 'Guardar',
    noChanges: 'Sin cambios',
    publishedByWorkspace: 'Publicado por {workspaceName}',
    verifiedWorkspace: 'Espacio verificado',
    failed: 'Fallido',
    queued: 'En cola',
    generating: 'Generando',
    removeAvatar: 'Quitar {name}',
    avatarSeedPrompts: AVATAR_SEED_PROMPTS,
  },
  generationSuggestions: {
    generatingAssets: 'Generando recursos',
    starting: 'Iniciando...',
    stoppedByUser: 'Detenido por el usuario',
    regenerateSuggestions: 'Regenerar sugerencias',
    regenerateSuggestionsDescription:
      'Esto volverá a analizar tu producto y generará nuevas sugerencias en función de la imagen principal actual y la galería. Las sugerencias actuales se reemplazarán.',
    regenerate: 'Regenerar',
    cancel: 'Cancelar',
    runAllSuggestions: 'Ejecutar todas las sugerencias',
    runAllSuggestionsDescription:
      'Esto ejecutará todas las sugerencias ({totalImages} imágenes en total). Puede tardar un poco, pero los recursos irán apareciendo a medida que terminen.',
    fullAutoMode: 'Modo totalmente automático',
    fullAutoModeDescription:
      'El sistema decidirá automáticamente qué generar a continuación según la imagen principal actual y la galería, y empezará de inmediato.',
    startFullAutoMode: 'Iniciar modo automático',
    imageSingular: 'imagen',
    imagePlural: 'imágenes',
    numberOfImagesToGenerate: 'Número de imágenes que se generarán',
    generateBetween: 'Genera entre 1 y {count} {label}',
    editSuggestion: 'Editar sugerencia',
    maxImagesSavedForSuggestion:
      'Máximo de imágenes guardado para esta sugerencia',
    savesDefaultMaxForFutureRuns:
      'Guarda el máximo predeterminado para futuras ejecuciones de esta sugerencia.',
    saveDefaults: 'Guardar valores predeterminados',
    aiSuggestions: 'Sugerencias de IA',
    generatingImageCount: 'Generando {count} imagen(es)...',
    stopGeneration: 'Detener generación',
    runAll: 'Ejecutar todo',
    fullAuto: 'Automático',
    analyzingProductAndGeneratingSuggestions:
      'Analizando el producto y generando sugerencias...',
    noSuggestionsYet: 'Aún no hay sugerencias',
    noSuggestionsDescription:
      'Genera sugerencias con IA para ayudarte a crear mejores recursos.',
    generateSuggestions: 'Generar sugerencias',
    failedToLoadSuggestions:
      'No se pudieron cargar las sugerencias. Inténtalo de nuevo.',
    generateImageCountCta: 'Generar {count} {label}',
  },
  walkthrough: {
    productSuggestionsTitle: 'Generar recursos',
    productSuggestionsDescription:
      'Genera con un clic recursos para distintos usos, desde listados de producto y escenas lifestyle hasta anuncios sociales y UGC. Elige un tipo y IMAI crea un lote al instante.',
    productAvatarsTitle: 'Crear o usar avatares',
    productAvatarsDescription:
      'Construye un flujo de personajes coherente para este producto. Si todavía no tienes avatares, ve a Diseño para crearlos. Si ya los tienes, selecciona hasta 5 aquí para generaciones sociales y lifestyle.',
    productTabsTitle: 'Elige tu superficie de trabajo',
    productTabsDescription:
      'Estas pestañas hacen más que organizar resultados: cambian el producto entre distintos modos de trabajo. Media Studio sirve para crear recursos visuales, Datos del catálogo para información lista para e-commerce, Estampados y patrones para exploración repetible y Planos técnicos para flujos de producción.',
    productGalleryTitle: 'Tu galería de recursos',
    productGalleryDescription:
      'Aquí viven todas las imágenes, videos y modelos 3D generados. Pasa el cursor sobre cualquier recurso para descargarlo, ampliarlo, editarlo con inpainting o mencionarlo en el chat para seguir refinándolo.',
    productAddAssetTitle: 'Añadir o generar recursos',
    productAddAssetDescription:
      'Sube tus propias fotos y videos o genera nuevos para este grupo. Cada grupo de recursos organiza el contenido por campaña, estilo o caso de uso.',
    productChatTabsTitle: 'Espacio de chat',
    productChatTabsDescription:
      'Este es un espacio de trabajo específico del producto, no un chat genérico. El hilo activo mantiene una línea de pensamiento enfocada, mientras que Chats te deja moverte entre conversaciones guardadas para este producto y versión.',
    productChatNewTitle: 'Explora nuevas direcciones',
    productChatNewDescription:
      'Usa Nuevo chat para iniciar una exploración nueva sin sobrescribir la conversación actual. Es la forma más rápida de comparar direcciones creativas o estratégicas lado a lado.',
    productChatInputTitle: 'Usa menciones y contexto',
    productChatInputDescription:
      'Este compositor entiende el producto, la versión y la superficie de trabajo actuales. Menciona recursos, grupos y referencias directamente en el chat para que IMAI trabaje con contexto preciso y no solo con texto.',
    productVersionsTitle: 'Historial de versiones',
    productVersionsDescription:
      'Cada versión captura una instantánea de tu producto. Crea nuevas versiones para explorar distintas direcciones sin perder el trabajo anterior.',
    mobileProductTabsTitle: 'Elige tu superficie de trabajo',
    mobileProductTabsDescription:
      'Desliza por estas pestañas para cambiar entre los distintos modos de trabajo del producto. Media Studio es para visuales, Datos del catálogo para listados, Estampados y patrones para exploración repetible y Planos técnicos para producción.',
    mobileProductChatTitle: 'Chat del espacio del producto',
    mobileProductChatDescription:
      'Toca para abrir un chat ligado a este producto y a la superficie que estás viendo. Pide variaciones, cambios de estilo o próximos pasos, e IMAI responderá con contexto del producto.',
  },
};

const FR_COPY: ProductStaticCopy = {
  workspace: {
    mediaStudio: 'Media Studio',
    catalogueData: 'Données catalogue',
    printsAndPatterns: 'Imprimés et motifs',
    technicalBlueprints: 'Technical Studio',
    manufacturing: 'Fabrication',
    threeDModels: 'Modèles 3D',
    creating: 'Création...',
    newChat: 'Nouveau chat',
    chatWithThisProduct: 'Discuter de ce produit',
    newTab: 'Nouvel onglet',
    chats: 'Chats',
    failedToProcessMessage: 'Impossible de traiter le message',
    askAnythingOrMentionAsset: 'Demandez ou utilisez @',
    main: 'Principal',
    mainAsset: 'Asset principal',
    generatingImage: 'Génération de l’image',
    productListing: 'Fiche produit',
    socialMedia: 'Réseaux sociaux',
    lifestyle: 'Lifestyle',
    detail: 'Détail',
    layoutView: 'Vue de mise en page',
    groupedView: 'Vue groupée',
    allAssetsView: 'Tous les assets',
    listView: 'Vue liste',
    exitSelectionMode: 'Quitter le mode sélection',
    selecting: 'Sélection',
    selectAssets: 'Sélectionner des assets',
    select: 'Sélectionner',
    selectedCount: '{count} sélectionnés',
    clearSelection: 'Effacer la sélection',
    selectAll: 'Tout sélectionner',
    clear: 'Effacer',
    allCount: 'Tous ({count})',
    downloadSelected: 'Télécharger la sélection',
    preparing: 'Préparation...',
    download: 'Télécharger',
    mentionSelectedInChat: 'Mentionner la sélection dans le chat',
    mention: 'Mentionner',
    deleteSelected: 'Supprimer la sélection',
    delete: 'Supprimer',
    editAsset: 'Modifier l’asset',
    viewFullScreen: 'Afficher en plein écran',
    deleteAsset: 'Supprimer l’asset',
    deleteThisAsset: 'Supprimer cet asset ?',
    selectAsset: 'Sélectionner un asset',
    openGenerationChat: 'Ouvrir le chat de génération',
    chooseAssetToDiscuss: 'Choisissez un asset à discuter dans le chat',
    mainAssetAlt: 'asset principal de {productName}',
    version: 'Version',
    noVersionSelected: 'Aucune version sélectionnée',
    noMainAsset: 'Aucun asset principal',
    selected: 'Sélectionné',
    newLabel: 'Nouveau',
    moreVersionsAvailable: '{count} autres versions disponibles',
    selectedAsset: 'Asset sélectionné',
    videoAsset: 'Asset vidéo',
    threeDModel: 'Modèle 3D',
    replaceAsset: 'Remplacer l’asset',
    maximizeAsset: 'Agrandir l’asset',
    minimizeAsset: 'Réduire l’asset',
    assetPreview: 'Aperçu de l’asset',
    imageLabel: 'Image',
    openAsset: 'Ouvrir l’asset',
    selectFileForDownload: 'Sélectionner le fichier à télécharger',
    deselectFile: 'Désélectionner le fichier',
    createdRelative: 'Créé {time}',
    updatedRelative: 'Mis à jour {time}',
    generatingVideo: 'Génération de la vidéo',
    fineTuningImage: 'Ajustement fin de l’image',
    changeSelectionWhilePinned:
      'Impossible de modifier la sélection tant que l’asset est épinglé. Désépinglez-le d’abord.',
    selectVersionBeforePreparingDownload:
      'Sélectionnez une version avant de préparer un téléchargement.',
    selectAtLeastOneFile: 'Sélectionnez au moins un fichier.',
    preparingFilesForDownload:
      'Préparation de {count} fichier(s) pour le téléchargement',
    addedAssetsToChat: '{count} asset(s) ajouté(s) au chat',
    deleteAssetsTitle: 'Supprimer les assets ?',
    deleteAssetsDescription:
      'Cette action est irréversible. Les assets sélectionnés seront supprimés définitivement.',
    deletingAssets: 'Suppression de {count} asset(s)...',
    deletedAssets: '{count} asset(s) supprimé(s)',
    somethingWentWrong: 'Un problème est survenu',
    changeLayoutView: 'Changer la vue de mise en page',
    deleteAssetQuestion: 'Supprimer cet asset ?',
    deletingAsset: 'Suppression de l’asset...',
    assetDeleted: 'Asset supprimé',
    generatingAdditionalAssets: 'Génération d’assets supplémentaires...',
    creatingMoreAssets: 'Création de nouveaux assets pour votre produit',
    generatingMainAsset: 'Génération de l’asset principal...',

    logoReference: {
      title: 'Référence du logo',
      description:
        'Conservez ici une référence de logo réutilisable pour que les générations futures préservent la bonne marque, les indices de placement et le traitement des couleurs.',
      autoBadge: 'Auto',
      manualBadge: 'Manuel',
      addManual: 'Téléversez votre logo',
      editManual: 'Téléversez votre logo',
      reextract: "Relancer l'extraction",
      processing: 'Génération de la référence du logo.',
      noSavedReference: 'Aucune référence de logo enregistrée pour le moment.',
      saveFailed: "Échec de l'enregistrement de la référence du logo.",
      uploadFailed: 'Échec du téléversement de la référence du logo.',
      logoTextLabel: 'Texte du logo',
      logoTextPlaceholder: 'Wordmark de la marque ou texte du logo',
      contextLabel: 'Contexte',
      contextPlaceholder:
        'Exemple : petite broderie crème ton sur ton sur la poitrine en haut à gauche sur un tricot noir côtelé',
    },
  },
  avatarSelector: {
    failedToSaveAvatarSelection:
      'Impossible d’enregistrer la sélection des avatars',
    failedToRemoveAvatar: 'Impossible de retirer l’avatar',
    publishedIndividually: 'Publié individuellement',
    createAvatarCharacterPrompt: 'Créer un personnage avatar : {seed}',
    addAvatar: 'Ajouter un avatar',
    editAvatars: 'Modifier les avatars',
    createAvatars: 'Créer des avatars',
    createAvatarsDescription:
      'Les avatars vous aident à générer des scènes lifestyle et sociales avec un personnage cohérent.',
    goToDesign: 'Aller à Design',
    useAvatars: 'Utiliser des avatars',
    useAvatarsDescription:
      'Choisissez jusqu’à 5 avatars pour guider les générations lifestyle et sociales.',
    on: 'Activé',
    off: 'Désactivé',
    avatarsEnabledDescription:
      'Les avatars sélectionnés seront utilisés pour les scènes lifestyle et sociales.',
    avatarsDisabledDescription:
      'Les avatars sont désactivés. Activez-les pour les utiliser dans les générations.',
    selectAvatars: 'Sélectionner des avatars',
    selectAvatarsDescription:
      'Choisissez jusqu’à 5 avatars de votre espace de travail ou de sets publiés pour les scènes lifestyle et sociales.',
    searchAvatars: 'Rechercher des avatars...',
    noAvatarsFound: 'Aucun avatar trouvé',
    tryDifferentSearchTerm: 'Essayez un autre terme de recherche',
    noAvatarsAvailable: 'Aucun avatar disponible',
    createAvatarsFirst: 'Créez d’abord des avatars dans la section Avatars.',
    yourWorkspace: 'Avatars de votre espace de travail',
    sharedWithYou: 'Avatars partagés',
    privateAndPublished: 'Privés et publiés',
    private: 'Avatars privés',
    publishedSets: 'Sets publiés',
    verifiedPublishers: 'Éditeurs vérifiés',
    avatarCount: '{count} avatars',
    selected: 'Sélectionnés :',
    cancel: 'Annuler',
    save: 'Enregistrer',
    noChanges: 'Aucun changement',
    publishedByWorkspace: 'Publié par {workspaceName}',
    verifiedWorkspace: 'Espace de travail vérifié',
    failed: 'Échec',
    queued: 'En file',
    generating: 'Génération',
    removeAvatar: 'Retirer {name}',
    avatarSeedPrompts: AVATAR_SEED_PROMPTS,
  },
  generationSuggestions: {
    generatingAssets: 'Génération des assets',
    starting: 'Démarrage...',
    stoppedByUser: 'Arrêté par l’utilisateur',
    regenerateSuggestions: 'Régénérer les suggestions',
    regenerateSuggestionsDescription:
      'Le produit sera de nouveau analysé et de nouvelles suggestions seront générées à partir de l’image principale actuelle et de la galerie. Les suggestions existantes seront remplacées.',
    regenerate: 'Régénérer',
    cancel: 'Annuler',
    runAllSuggestions: 'Lancer toutes les suggestions',
    runAllSuggestionsDescription:
      'Cela lancera toutes les suggestions ({totalImages} images au total). Cela peut prendre un peu de temps, mais les assets apparaîtront au fur et à mesure.',
    fullAutoMode: 'Mode entièrement automatique',
    fullAutoModeDescription:
      'Le système décidera automatiquement quoi générer ensuite selon l’image principale actuelle et la galerie, puis démarrera immédiatement.',
    startFullAutoMode: 'Démarrer le mode automatique',
    imageSingular: 'image',
    imagePlural: 'images',
    numberOfImagesToGenerate: 'Nombre d’images à générer',
    generateBetween: 'Générer entre 1 et {count} {label}',
    editSuggestion: 'Modifier la suggestion',
    maxImagesSavedForSuggestion:
      'Nombre maximum d’images enregistré pour cette suggestion',
    savesDefaultMaxForFutureRuns:
      'Enregistre le maximum par défaut pour les prochaines exécutions de cette suggestion.',
    saveDefaults: 'Enregistrer par défaut',
    aiSuggestions: 'Suggestions IA',
    generatingImageCount: 'Génération de {count} image(s)...',
    stopGeneration: 'Arrêter la génération',
    runAll: 'Tout lancer',
    fullAuto: 'Auto',
    analyzingProductAndGeneratingSuggestions:
      'Analyse du produit et génération des suggestions...',
    noSuggestionsYet: 'Aucune suggestion pour le moment',
    noSuggestionsDescription:
      'Générez des suggestions IA pour créer de meilleurs assets.',
    generateSuggestions: 'Générer des suggestions',
    failedToLoadSuggestions:
      'Impossible de charger les suggestions. Veuillez réessayer.',
    generateImageCountCta: 'Générer {count} {label}',
  },
  walkthrough: {
    productSuggestionsTitle: 'Générer des assets',
    productSuggestionsDescription:
      'Générez en un clic des assets pour différents usages, des fiches produit aux scènes lifestyle, publicités sociales et contenus UGC. Choisissez un type et IMAI crée un lot instantanément.',
    productAvatarsTitle: 'Créer ou utiliser des avatars',
    productAvatarsDescription:
      'Construisez un flux de personnages cohérent pour ce produit. Si vous n’avez pas encore d’avatars, allez dans Design pour les créer. Sinon, sélectionnez-en jusqu’à 5 ici pour les générations lifestyle et sociales.',
    productTabsTitle: 'Choisissez votre surface de travail',
    productTabsDescription:
      'Ces onglets ne servent pas seulement à organiser les résultats, ils font basculer le produit entre différents modes de travail. Media Studio sert à la création visuelle, Données catalogue aux informations prêtes pour l’e-commerce, Imprimés et motifs à l’exploration répétable, et Plans techniques aux flux de production.',
    productGalleryTitle: 'Votre galerie d’assets',
    productGalleryDescription:
      'Toutes les images, vidéos et modèles 3D générés vivent ici. Survolez un asset pour le télécharger, l’upscaler, le modifier avec l’inpainting ou le mentionner dans le chat pour aller plus loin.',
    productAddAssetTitle: 'Ajouter ou générer des assets',
    productAddAssetDescription:
      'Importez vos propres photos et vidéos ou générez-en de nouveaux pour ce groupe. Chaque groupe d’assets organise le contenu par campagne, style ou cas d’usage.',
    productChatTabsTitle: 'Espace de chat',
    productChatTabsDescription:
      'Il s’agit d’un espace de travail spécifique au produit, pas d’un chat générique. Le fil actif garde une seule ligne de réflexion, tandis que Chats vous permet de naviguer entre les conversations enregistrées pour ce produit et cette version.',
    productChatNewTitle: 'Explorer de nouvelles directions',
    productChatNewDescription:
      'Utilisez Nouveau chat pour démarrer une exploration sans écraser la conversation actuelle. C’est la manière la plus rapide de comparer des directions créatives ou stratégiques côte à côte.',
    productChatInputTitle: 'Utilisez mentions et contexte',
    productChatInputDescription:
      'Ce composeur comprend le produit, la version et la surface de travail actuels. Mentionnez directement des assets, des groupes et d’autres références pour qu’IMAI travaille à partir d’un contexte précis.',
    productVersionsTitle: 'Historique des versions',
    productVersionsDescription:
      'Chaque version capture un instantané de votre produit. Créez de nouvelles versions pour explorer différentes directions sans perdre votre travail précédent.',
    mobileProductTabsTitle: 'Choisissez votre surface de travail',
    mobileProductTabsDescription:
      'Faites défiler ces onglets pour passer d’un mode de travail à l’autre. Media Studio sert aux visuels, Données catalogue aux listings, Imprimés et motifs à l’exploration répétable, et Plans techniques aux flux de production.',
    mobileProductChatTitle: 'Chat de l’espace produit',
    mobileProductChatDescription:
      'Touchez ici pour ouvrir un chat lié à ce produit et à la surface affichée. Demandez des variations, des changements de style ou les prochaines étapes, et IMAI répondra avec le contexte du produit.',
  },
};

const HI_COPY: ProductStaticCopy = {
  workspace: {
    mediaStudio: 'मीडिया स्टूडियो',
    catalogueData: 'कैटलॉग डेटा',
    printsAndPatterns: 'प्रिंट्स और पैटर्न',
    technicalBlueprints: 'Technical Studio',
    manufacturing: 'विनिर्माण',
    threeDModels: '3D मॉडल',
    creating: 'बनाया जा रहा है...',
    newChat: 'नई चैट',
    chatWithThisProduct: 'इस प्रोडक्ट के बारे में चैट करें',
    newTab: 'नया टैब',
    chats: 'चैट्स',
    failedToProcessMessage: 'संदेश प्रोसेस नहीं हो सका',
    askAnythingOrMentionAsset: 'पूछें या @ उपयोग करें',
    main: 'मुख्य',
    mainAsset: 'मुख्य एसेट',
    generatingImage: 'इमेज जनरेट हो रही है',
    productListing: 'प्रोडक्ट लिस्टिंग',
    socialMedia: 'सोशल मीडिया',
    lifestyle: 'लाइफस्टाइल',
    detail: 'डिटेल',
    layoutView: 'लेआउट व्यू',
    groupedView: 'ग्रुप्ड व्यू',
    allAssetsView: 'सभी एसेट्स',
    listView: 'लिस्ट व्यू',
    exitSelectionMode: 'सेलेक्शन मोड से बाहर निकलें',
    selecting: 'चयन जारी है',
    selectAssets: 'एसेट्स चुनें',
    select: 'चुनें',
    selectedCount: '{count} चुने गए',
    clearSelection: 'चयन साफ करें',
    selectAll: 'सभी चुनें',
    clear: 'साफ करें',
    allCount: 'सभी ({count})',
    downloadSelected: 'चुने हुए डाउनलोड करें',
    preparing: 'तैयार किया जा रहा है...',
    download: 'डाउनलोड',
    mentionSelectedInChat: 'चुने हुए को चैट में मेंशन करें',
    mention: 'मेंशन',
    deleteSelected: 'चुने हुए हटाएँ',
    delete: 'हटाएँ',
    editAsset: 'एसेट संपादित करें',
    viewFullScreen: 'फुल स्क्रीन में देखें',
    deleteAsset: 'एसेट हटाएँ',
    deleteThisAsset: 'क्या इस एसेट को हटाना है?',
    selectAsset: 'एसेट चुनें',
    openGenerationChat: 'जनरेशन चैट खोलें',
    chooseAssetToDiscuss: 'चैट में चर्चा करने के लिए कोई एसेट चुनें',
    mainAssetAlt: '{productName} का मुख्य एसेट',
    version: 'संस्करण',
    noVersionSelected: 'कोई संस्करण चयनित नहीं है',
    noMainAsset: 'कोई मुख्य एसेट नहीं है',
    selected: 'चयनित',
    newLabel: 'नया',
    moreVersionsAvailable: '{count} और संस्करण उपलब्ध हैं',
    selectedAsset: 'चयनित एसेट',
    videoAsset: 'वीडियो एसेट',
    threeDModel: '3D मॉडल',
    replaceAsset: 'एसेट बदलें',
    maximizeAsset: 'एसेट बड़ा करें',
    minimizeAsset: 'एसेट छोटा करें',
    assetPreview: 'एसेट पूर्वावलोकन',
    imageLabel: 'छवि',
    openAsset: 'एसेट खोलें',
    selectFileForDownload: 'डाउनलोड के लिए फ़ाइल चुनें',
    deselectFile: 'फ़ाइल का चयन हटाएँ',
    createdRelative: '{time} बनाया गया',
    updatedRelative: '{time} अपडेट किया गया',
    generatingVideo: 'वीडियो बनाया जा रहा है',
    fineTuningImage: 'छवि को बेहतर बनाया जा रहा है',
    changeSelectionWhilePinned:
      'एसेट पिन होने पर चयन बदला नहीं जा सकता। पहले अनपिन करें।',
    selectVersionBeforePreparingDownload:
      'डाउनलोड तैयार करने से पहले एक संस्करण चुनें।',
    selectAtLeastOneFile: 'कम से कम एक फ़ाइल चुनें।',
    preparingFilesForDownload:
      'डाउनलोड के लिए {count} फ़ाइल तैयार की जा रही है',
    addedAssetsToChat: '{count} एसेट चैट में जोड़े गए',
    deleteAssetsTitle: 'एसेट हटाएँ?',
    deleteAssetsDescription:
      'इस क्रिया को वापस नहीं किया जा सकता। चुने हुए एसेट स्थायी रूप से हटा दिए जाएंगे।',
    deletingAssets: '{count} एसेट हटाए जा रहे हैं...',
    deletedAssets: '{count} एसेट हटा दिए गए',
    somethingWentWrong: 'कुछ गलत हो गया',
    changeLayoutView: 'लेआउट दृश्य बदलें',
    deleteAssetQuestion: 'क्या इस एसेट को हटाना है?',
    deletingAsset: 'एसेट हटाया जा रहा है...',
    assetDeleted: 'एसेट हटा दिया गया',
    generatingAdditionalAssets: 'अतिरिक्त एसेट बनाए जा रहे हैं...',
    creatingMoreAssets: 'आपके उत्पाद के लिए और एसेट बनाए जा रहे हैं',
    generatingMainAsset: 'मुख्य एसेट बनाया जा रहा है...',

    logoReference: {
      title: 'लोगो रेफरेंस',
      description:
        'यहाँ एक पुन: उपयोग योग्य लोगो रेफरेंस रखें ताकि भविष्य की जनरेशन में सही मार्क, प्लेसमेंट संकेत और रंग ट्रीटमेंट सुरक्षित रहे।',
      autoBadge: 'ऑटो',
      manualBadge: 'मैन्युअल',
      addManual: 'अपना लोगो अपलोड करें',
      editManual: 'अपना लोगो अपलोड करें',
      reextract: 'एक्सट्रैक्शन फिर से चलाएँ',
      processing: 'लोगो रेफरेंस बना रहे हैं।',
      noSavedReference: 'अभी कोई लोगो रेफरेंस सेव नहीं है।',
      saveFailed: 'लोगो रेफरेंस सेव नहीं हो सका।',
      uploadFailed: 'लोगो रेफरेंस अपलोड नहीं हो सका।',
      logoTextLabel: 'लोगो टेक्स्ट',
      logoTextPlaceholder: 'ब्रांड वर्डमार्क या लोगो टेक्स्ट',
      contextLabel: 'संदर्भ',
      contextPlaceholder:
        'उदाहरण: रिब्ड काले निट के ऊपर ऊपरी-बाएँ छाती पर छोटी टोनल क्रीम कढ़ाई',
    },
  },
  avatarSelector: {
    failedToSaveAvatarSelection: 'अवतार चयन सहेजा नहीं जा सका',
    failedToRemoveAvatar: 'अवतार हटाया नहीं जा सका',
    publishedIndividually: 'अलग से प्रकाशित',
    createAvatarCharacterPrompt: 'एक अवतार कैरेक्टर बनाएं: {seed}',
    addAvatar: 'अवतार जोड़ें',
    editAvatars: 'अवतार संपादित करें',
    createAvatars: 'अवतार बनाएं',
    createAvatarsDescription:
      'अवतार आपको एक ही कैरेक्टर के साथ लाइफस्टाइल और सोशल शॉट्स बनाने में मदद करते हैं.',
    goToDesign: 'डिज़ाइन पर जाएँ',
    useAvatars: 'अवतार इस्तेमाल करें',
    useAvatarsDescription:
      'लाइफस्टाइल और सोशल इमेज जनरेशन के लिए अधिकतम 5 अवतार चुनें.',
    on: 'चालू',
    off: 'बंद',
    avatarsEnabledDescription:
      'चुने गए अवतार लाइफस्टाइल और सोशल शॉट्स में उपयोग होंगे.',
    avatarsDisabledDescription:
      'अवतार बंद हैं. इन्हें उपयोग करने के लिए चालू करें.',
    selectAvatars: 'अवतार चुनें',
    selectAvatarsDescription:
      'लाइफस्टाइल और सोशल शॉट्स के लिए अपने वर्कस्पेस या प्रकाशित सेट्स से अधिकतम 5 अवतार चुनें.',
    searchAvatars: 'अवतार खोजें...',
    noAvatarsFound: 'कोई अवतार नहीं मिला',
    tryDifferentSearchTerm: 'कोई दूसरा खोज शब्द आज़माएँ',
    noAvatarsAvailable: 'कोई अवतार उपलब्ध नहीं है',
    createAvatarsFirst: 'पहले अवतार सेक्शन में अवतार बनाएं.',
    yourWorkspace: 'आपके वर्कस्पेस अवतार',
    sharedWithYou: 'आपके साथ साझा किए गए अवतार',
    privateAndPublished: 'निजी और प्रकाशित',
    private: 'निजी अवतार',
    publishedSets: 'प्रकाशित सेट्स',
    verifiedPublishers: 'सत्यापित प्रकाशक',
    avatarCount: '{count} अवतार',
    selected: 'चयनित:',
    cancel: 'रद्द करें',
    save: 'सहेजें',
    noChanges: 'कोई बदलाव नहीं',
    publishedByWorkspace: '{workspaceName} द्वारा प्रकाशित',
    verifiedWorkspace: 'सत्यापित वर्कस्पेस',
    failed: 'विफल',
    queued: 'कतार में',
    generating: 'जनरेट हो रहा है',
    removeAvatar: '{name} हटाएँ',
    avatarSeedPrompts: AVATAR_SEED_PROMPTS,
  },
  generationSuggestions: {
    generatingAssets: 'एसेट्स जनरेट हो रहे हैं',
    starting: 'शुरू हो रहा है...',
    stoppedByUser: 'उपयोगकर्ता द्वारा रोका गया',
    regenerateSuggestions: 'सुझाव फिर से बनाएं',
    regenerateSuggestionsDescription:
      'यह आपके प्रोडक्ट का फिर से विश्लेषण करेगा और वर्तमान मुख्य इमेज व गैलरी के आधार पर नए सुझाव बनाएगा. मौजूदा सुझाव बदल दिए जाएँगे.',
    regenerate: 'फिर से बनाएं',
    cancel: 'रद्द करें',
    runAllSuggestions: 'सभी सुझाव चलाएँ',
    runAllSuggestionsDescription:
      'यह सभी सुझाव चलाएगा (कुल {totalImages} इमेज). इसमें कुछ समय लग सकता है, लेकिन एसेट्स धीरे-धीरे दिखाई देंगे.',
    fullAutoMode: 'फुल ऑटो मोड',
    fullAutoModeDescription:
      'सिस्टम आपकी मुख्य इमेज और मौजूदा गैलरी के आधार पर तय करेगा कि आगे क्या जनरेट करना है, और तुरंत शुरू करेगा.',
    startFullAutoMode: 'फुल ऑटो मोड शुरू करें',
    imageSingular: 'इमेज',
    imagePlural: 'इमेजें',
    numberOfImagesToGenerate: 'जनरेट की जाने वाली इमेजों की संख्या',
    generateBetween: '1 से {count} {label} के बीच जनरेट करें',
    editSuggestion: 'सुझाव संपादित करें',
    maxImagesSavedForSuggestion: 'इस सुझाव के लिए सहेजी गई अधिकतम इमेजें',
    savesDefaultMaxForFutureRuns:
      'यह इस सुझाव के भविष्य के रन के लिए डिफ़ॉल्ट अधिकतम संख्या सहेजता है.',
    saveDefaults: 'डिफ़ॉल्ट सहेजें',
    aiSuggestions: 'AI सुझाव',
    generatingImageCount: '{count} इमेज जनरेट हो रही है...',
    stopGeneration: 'जनरेशन रोकें',
    runAll: 'सभी चलाएँ',
    fullAuto: 'फुल ऑटो',
    analyzingProductAndGeneratingSuggestions:
      'प्रोडक्ट का विश्लेषण किया जा रहा है और सुझाव बनाए जा रहे हैं...',
    noSuggestionsYet: 'अभी कोई सुझाव नहीं है',
    noSuggestionsDescription:
      'बेहतर एसेट्स बनाने के लिए AI-सहायता वाले सुझाव जनरेट करें.',
    generateSuggestions: 'सुझाव जनरेट करें',
    failedToLoadSuggestions: 'सुझाव लोड नहीं हो सके. कृपया फिर से कोशिश करें.',
    generateImageCountCta: '{count} {label} जनरेट करें',
  },
  walkthrough: {
    productSuggestionsTitle: 'एसेट्स जनरेट करें',
    productSuggestionsDescription:
      'प्रोडक्ट लिस्टिंग, लाइफस्टाइल शॉट्स, सोशल ऐड्स और UGC जैसे अलग-अलग उपयोगों के लिए एक क्लिक में एसेट्स जनरेट करें. एक प्रकार चुनें और IMAI तुरंत बैच तैयार कर देगा.',
    productAvatarsTitle: 'अवतार बनाएं या उपयोग करें',
    productAvatarsDescription:
      'इस प्रोडक्ट के लिए एक स्थिर कैरेक्टर वर्कफ़्लो बनाएं. यदि आपके पास अभी अवतार नहीं हैं, तो उन्हें बनाने के लिए डिज़ाइन पर जाएँ. यदि हैं, तो लाइफस्टाइल और सोशल जनरेशन के लिए यहाँ से अधिकतम 5 चुनें.',
    productTabsTitle: 'अपना वर्क सरफेस चुनें',
    productTabsDescription:
      'ये टैब सिर्फ आउटपुट व्यवस्थित नहीं करते, बल्कि प्रोडक्ट को अलग-अलग वर्क मोड में ले जाते हैं. मीडिया स्टूडियो विज़ुअल्स के लिए है, कैटलॉग डेटा कॉमर्स-रेडी जानकारी के लिए, प्रिंट्स और पैटर्न दोहराए जाने वाले डिज़ाइन एक्सप्लोरेशन के लिए, और टेक्निकल ब्लूप्रिंट्स प्रोडक्शन वर्कफ़्लो के लिए.',
    productGalleryTitle: 'आपकी एसेट गैलरी',
    productGalleryDescription:
      'सभी जनरेट की गई इमेज, वीडियो और 3D मॉडल यहाँ रहते हैं. किसी भी एसेट पर होवर करके उसे डाउनलोड करें, अपस्केल करें, इनपेंटिंग से संपादित करें, या आगे सुधार के लिए चैट में मेंशन करें.',
    productAddAssetTitle: 'एसेट्स जोड़ें या जनरेट करें',
    productAddAssetDescription:
      'अपनी फ़ोटो और वीडियो अपलोड करें या इस समूह के लिए नए एसेट्स जनरेट करें. हर एसेट समूह कंटेंट को कैंपेन, स्टाइल या उपयोग-स्थिति के हिसाब से व्यवस्थित करता है.',
    productChatTabsTitle: 'चैट वर्कस्पेस',
    productChatTabsDescription:
      'यह एक प्रोडक्ट-विशिष्ट वर्कस्पेस है, सामान्य चैट नहीं. सक्रिय थ्रेड एक सोच को केंद्रित रखता है, जबकि Chats आपको इस प्रोडक्ट और वर्ज़न की सेव की गई बातचीतों के बीच ले जाता है.',
    productChatNewTitle: 'नई दिशाओं में जाएँ',
    productChatNewDescription:
      'मौजूदा बातचीत को बदले बिना नई खोज शुरू करने के लिए New chat का उपयोग करें. अलग-अलग क्रिएटिव या रणनीतिक दिशाओं की तुलना करने का यह सबसे तेज़ तरीका है.',
    productChatInputTitle: 'मेंशन्स और कॉन्टेक्स्ट का उपयोग करें',
    productChatInputDescription:
      'यह कंपोज़र मौजूदा प्रोडक्ट, वर्ज़न और वर्क सरफेस को समझता है. एसेट्स, समूहों और रेफरेंसेज़ को सीधे चैट में मेंशन करें ताकि IMAI केवल टेक्स्ट नहीं बल्कि सटीक संदर्भ के साथ काम करे.',
    productVersionsTitle: 'वर्ज़न हिस्ट्री',
    productVersionsDescription:
      'हर वर्ज़न आपके प्रोडक्ट का एक स्नैपशॉट पकड़ता है. पिछला काम खोए बिना अलग दिशाएँ देखने के लिए नए वर्ज़न बनाएं.',
    mobileProductTabsTitle: 'अपना वर्क सरफेस चुनें',
    mobileProductTabsDescription:
      'अलग-अलग वर्क मोड में जाने के लिए इन टैब्स को स्वाइप करें. मीडिया स्टूडियो विज़ुअल्स के लिए, कैटलॉग डेटा लिस्टिंग्स के लिए, प्रिंट्स और पैटर्न दोहराए जाने वाले डिज़ाइन एक्सप्लोरेशन के लिए, और टेक्निकल ब्लूप्रिंट्स प्रोडक्शन वर्कफ़्लो के लिए हैं.',
    mobileProductChatTitle: 'प्रोडक्ट वर्कस्पेस चैट',
    mobileProductChatDescription:
      'इस प्रोडक्ट और दिखाई दे रहे सरफेस से जुड़ी चैट खोलने के लिए टैप करें. वैरिएशन, स्टाइल बदलाव या अगले कदम पूछें, और IMAI प्रोडक्ट-संदर्भ के साथ जवाब देगा.',
  },
};

const IT_COPY: ProductStaticCopy = {
  workspace: {
    mediaStudio: 'Media Studio',
    catalogueData: 'Dati catalogo',
    printsAndPatterns: 'Stampe e pattern',
    technicalBlueprints: 'Technical Studio',
    manufacturing: 'Produzione',
    threeDModels: 'Modelli 3D',
    creating: 'Creazione in corso...',
    newChat: 'Nuova chat',
    chatWithThisProduct: 'Chatta su questo prodotto',
    newTab: 'Nuova scheda',
    chats: 'Chat',
    failedToProcessMessage: 'Impossibile elaborare il messaggio',
    askAnythingOrMentionAsset: 'Chiedi o usa @',
    main: 'Principale',
    mainAsset: 'Asset principale',
    generatingImage: 'Generazione immagine',
    productListing: 'Scheda prodotto',
    socialMedia: 'Social media',
    lifestyle: 'Lifestyle',
    detail: 'Dettaglio',
    layoutView: 'Vista layout',
    groupedView: 'Vista raggruppata',
    allAssetsView: 'Tutti gli asset',
    listView: 'Vista elenco',
    exitSelectionMode: 'Esci dalla modalità selezione',
    selecting: 'Selezione',
    selectAssets: 'Seleziona asset',
    select: 'Seleziona',
    selectedCount: '{count} selezionati',
    clearSelection: 'Cancella selezione',
    selectAll: 'Seleziona tutto',
    clear: 'Cancella',
    allCount: 'Tutti ({count})',
    downloadSelected: 'Scarica selezionati',
    preparing: 'Preparazione...',
    download: 'Scarica',
    mentionSelectedInChat: 'Menziona i selezionati in chat',
    mention: 'Menziona',
    deleteSelected: 'Elimina selezionati',
    delete: 'Elimina',
    editAsset: 'Modifica asset',
    viewFullScreen: 'Visualizza a schermo intero',
    deleteAsset: 'Elimina asset',
    deleteThisAsset: 'Eliminare questo asset?',
    selectAsset: 'Seleziona asset',
    openGenerationChat: 'Apri chat di generazione',
    chooseAssetToDiscuss: 'Scegli un asset da discutere nella chat',
    mainAssetAlt: 'asset principale di {productName}',
    version: 'Versione',
    noVersionSelected: 'Nessuna versione selezionata',
    noMainAsset: 'Nessun asset principale',
    selected: 'Selezionato',
    newLabel: 'Nuovo',
    moreVersionsAvailable: 'Sono disponibili altre {count} versioni',
    selectedAsset: 'Asset selezionato',
    videoAsset: 'Asset video',
    threeDModel: 'Modello 3D',
    replaceAsset: 'Sostituisci asset',
    maximizeAsset: 'Espandi asset',
    minimizeAsset: 'Riduci asset',
    assetPreview: 'Anteprima asset',
    imageLabel: 'Immagine',
    openAsset: 'Apri asset',
    selectFileForDownload: 'Seleziona il file da scaricare',
    deselectFile: 'Deseleziona file',
    createdRelative: 'Creato {time}',
    updatedRelative: 'Aggiornato {time}',
    generatingVideo: 'Generazione del video',
    fineTuningImage: 'Rifinitura dell’immagine',
    changeSelectionWhilePinned:
      'Impossibile cambiare la selezione mentre l’asset è bloccato. Sbloccalo prima.',
    selectVersionBeforePreparingDownload:
      'Seleziona una versione prima di preparare il download.',
    selectAtLeastOneFile: 'Seleziona almeno un file.',
    preparingFilesForDownload: 'Preparazione di {count} file per il download',
    addedAssetsToChat: 'Aggiunti {count} asset alla chat',
    deleteAssetsTitle: 'Eliminare gli asset?',
    deleteAssetsDescription:
      'Questa azione non può essere annullata. Gli asset selezionati verranno eliminati definitivamente.',
    deletingAssets: 'Eliminazione di {count} asset...',
    deletedAssets: 'Eliminati {count} asset',
    somethingWentWrong: 'Qualcosa è andato storto',
    changeLayoutView: 'Cambia vista layout',
    deleteAssetQuestion: 'Eliminare questo asset?',
    deletingAsset: 'Eliminazione dell’asset...',
    assetDeleted: 'Asset eliminato',
    generatingAdditionalAssets: 'Generazione di asset aggiuntivi...',
    creatingMoreAssets: 'Creazione di nuovi asset per il tuo prodotto',
    generatingMainAsset: 'Generazione dell’asset principale...',

    logoReference: {
      title: 'Riferimento logo',
      description:
        'Conserva qui un riferimento logo riutilizzabile in modo che le generazioni future preservino il marchio corretto, le indicazioni di posizionamento e il trattamento dei colori.',
      autoBadge: 'Auto',
      manualBadge: 'Manuale',
      addManual: 'Carica il tuo logo',
      editManual: 'Carica il tuo logo',
      reextract: "Riesegui l'estrazione",
      processing: 'Generazione del riferimento logo.',
      noSavedReference: 'Nessun riferimento logo salvato.',
      saveFailed: 'Salvataggio del riferimento logo fallito.',
      uploadFailed: 'Caricamento del riferimento logo fallito.',
      logoTextLabel: 'Testo del logo',
      logoTextPlaceholder: 'Wordmark del brand o testo del logo',
      contextLabel: 'Contesto',
      contextPlaceholder:
        'Esempio: piccolo ricamo ton sur ton crema sul petto in alto a sinistra su maglia nera a coste',
    },
  },
  avatarSelector: {
    failedToSaveAvatarSelection:
      'Impossibile salvare la selezione degli avatar',
    failedToRemoveAvatar: 'Impossibile rimuovere l’avatar',
    publishedIndividually: 'Pubblicato singolarmente',
    createAvatarCharacterPrompt: 'Crea un personaggio avatar: {seed}',
    addAvatar: 'Aggiungi avatar',
    editAvatars: 'Modifica avatar',
    createAvatars: 'Crea avatar',
    createAvatarsDescription:
      'Gli avatar ti aiutano a generare scene lifestyle e social con un personaggio coerente.',
    goToDesign: 'Vai a Design',
    useAvatars: 'Usa avatar',
    useAvatarsDescription:
      'Scegli fino a 5 avatar per guidare le generazioni lifestyle e social.',
    on: 'Attivo',
    off: 'Disattivo',
    avatarsEnabledDescription:
      'Gli avatar selezionati verranno usati per scene lifestyle e social.',
    avatarsDisabledDescription:
      'Gli avatar sono disattivati. Attivali per usarli nelle generazioni.',
    selectAvatars: 'Seleziona avatar',
    selectAvatarsDescription:
      'Scegli fino a 5 avatar dal tuo workspace o da set pubblicati per scene lifestyle e social.',
    searchAvatars: 'Cerca avatar...',
    noAvatarsFound: 'Nessun avatar trovato',
    tryDifferentSearchTerm: 'Prova un termine di ricerca diverso',
    noAvatarsAvailable: 'Nessun avatar disponibile',
    createAvatarsFirst: 'Crea prima gli avatar nella sezione Avatar.',
    yourWorkspace: 'Avatar del tuo workspace',
    sharedWithYou: 'Avatar condivisi',
    privateAndPublished: 'Privati e pubblicati',
    private: 'Avatar privati',
    publishedSets: 'Set pubblicati',
    verifiedPublishers: 'Publisher verificati',
    avatarCount: '{count} avatar',
    selected: 'Selezionati:',
    cancel: 'Annulla',
    save: 'Salva',
    noChanges: 'Nessuna modifica',
    publishedByWorkspace: 'Pubblicato da {workspaceName}',
    verifiedWorkspace: 'Workspace verificato',
    failed: 'Non riuscito',
    queued: 'In coda',
    generating: 'Generazione',
    removeAvatar: 'Rimuovi {name}',
    avatarSeedPrompts: AVATAR_SEED_PROMPTS,
  },
  generationSuggestions: {
    generatingAssets: 'Generazione asset',
    starting: 'Avvio...',
    stoppedByUser: 'Interrotto dall’utente',
    regenerateSuggestions: 'Rigenera suggerimenti',
    regenerateSuggestionsDescription:
      'Il prodotto verrà analizzato di nuovo e verranno creati nuovi suggerimenti in base all’immagine principale attuale e alla galleria. I suggerimenti esistenti verranno sostituiti.',
    regenerate: 'Rigenera',
    cancel: 'Annulla',
    runAllSuggestions: 'Esegui tutti i suggerimenti',
    runAllSuggestionsDescription:
      'Questo eseguirà tutti i suggerimenti ({totalImages} immagini totali). Potrebbe richiedere un po’ di tempo, ma gli asset appariranno man mano.',
    fullAutoMode: 'Modalità completamente automatica',
    fullAutoModeDescription:
      'Il sistema deciderà automaticamente cosa generare dopo in base all’immagine principale e alla galleria esistente, poi inizierà subito.',
    startFullAutoMode: 'Avvia modalità automatica',
    imageSingular: 'immagine',
    imagePlural: 'immagini',
    numberOfImagesToGenerate: 'Numero di immagini da generare',
    generateBetween: 'Genera tra 1 e {count} {label}',
    editSuggestion: 'Modifica suggerimento',
    maxImagesSavedForSuggestion:
      'Numero massimo di immagini salvato per questo suggerimento',
    savesDefaultMaxForFutureRuns:
      'Salva il massimo predefinito per le esecuzioni future di questo suggerimento.',
    saveDefaults: 'Salva predefiniti',
    aiSuggestions: 'Suggerimenti AI',
    generatingImageCount: 'Generazione di {count} immagini...',
    stopGeneration: 'Interrompi generazione',
    runAll: 'Esegui tutto',
    fullAuto: 'Auto',
    analyzingProductAndGeneratingSuggestions:
      'Analisi del prodotto e generazione dei suggerimenti...',
    noSuggestionsYet: 'Nessun suggerimento al momento',
    noSuggestionsDescription:
      'Genera suggerimenti con l’AI per creare asset migliori.',
    generateSuggestions: 'Genera suggerimenti',
    failedToLoadSuggestions: 'Impossibile caricare i suggerimenti. Riprova.',
    generateImageCountCta: 'Genera {count} {label}',
  },
  walkthrough: {
    productSuggestionsTitle: 'Genera asset',
    productSuggestionsDescription:
      'Genera con un clic asset per diversi casi d’uso, dalle schede prodotto e scene lifestyle fino ad annunci social e UGC. Scegli un tipo e IMAI crea subito un batch.',
    productAvatarsTitle: 'Crea o usa avatar',
    productAvatarsDescription:
      'Costruisci un flusso coerente di personaggi per questo prodotto. Se non hai ancora avatar, vai in Design per crearli. Se li hai già, selezionane fino a 5 qui per le generazioni lifestyle e social.',
    productTabsTitle: 'Scegli la tua superficie di lavoro',
    productTabsDescription:
      'Queste schede fanno più che organizzare gli output: spostano il prodotto tra diverse modalità di lavoro. Media Studio serve per la creazione visiva, Dati catalogo per informazioni pronte per il commerce, Stampe e pattern per esplorazioni ripetibili, e Blueprint tecnici per i flussi di produzione.',
    productGalleryTitle: 'La tua galleria asset',
    productGalleryDescription:
      'Tutte le immagini, i video e i modelli 3D generati vivono qui. Passa il cursore su un asset per scaricarlo, migliorarlo, modificarlo con inpainting o menzionarlo in chat per ulteriori raffinamenti.',
    productAddAssetTitle: 'Aggiungi o genera asset',
    productAddAssetDescription:
      'Carica le tue foto e i tuoi video oppure genera nuovi asset per questo gruppo. Ogni gruppo organizza i contenuti per campagna, stile o caso d’uso.',
    productChatTabsTitle: 'Workspace della chat',
    productChatTabsDescription:
      'Questo è un workspace specifico del prodotto, non una chat generica. Il thread attivo mantiene una singola direzione di pensiero, mentre Chat ti permette di passare tra conversazioni salvate per questo prodotto e questa versione.',
    productChatNewTitle: 'Apri nuove direzioni',
    productChatNewDescription:
      'Usa Nuova chat per iniziare un’esplorazione fresca senza sovrascrivere la conversazione attuale. È il modo più rapido per confrontare direzioni creative o strategiche fianco a fianco.',
    productChatInputTitle: 'Usa menzioni e contesto',
    productChatInputDescription:
      'Questo compositore comprende il prodotto, la versione e la superficie di lavoro attuali. Menziona direttamente asset, gruppi e riferimenti in chat così IMAI potrà lavorare con un contesto preciso invece del solo testo.',
    productVersionsTitle: 'Cronologia versioni',
    productVersionsDescription:
      'Ogni versione cattura un’istantanea del tuo prodotto. Crea nuove versioni per esplorare direzioni diverse senza perdere il lavoro precedente.',
    mobileProductTabsTitle: 'Scegli la tua superficie di lavoro',
    mobileProductTabsDescription:
      'Scorri tra queste schede per passare tra le diverse modalità di lavoro del prodotto. Media Studio è per i visual, Dati catalogo per i listing, Stampe e pattern per esplorazioni ripetibili e Blueprint tecnici per i flussi di produzione.',
    mobileProductChatTitle: 'Chat del workspace prodotto',
    mobileProductChatDescription:
      'Tocca qui per aprire una chat legata a questo prodotto e alla superficie che stai guardando. Chiedi variazioni, cambi di stile o prossimi passi e IMAI risponderà con contesto prodotto.',
  },
};

const JA_COPY: ProductStaticCopy = {
  workspace: {
    mediaStudio: 'メディアスタジオ',
    catalogueData: 'カタログデータ',
    printsAndPatterns: 'プリントとパターン',
    technicalBlueprints: 'Technical Studio',
    manufacturing: '製造',
    threeDModels: '3Dモデル',
    creating: '作成中...',
    newChat: '新しいチャット',
    chatWithThisProduct: 'この商品についてチャット',
    newTab: '新しいタブ',
    chats: 'チャット',
    failedToProcessMessage: 'メッセージを処理できませんでした',
    askAnythingOrMentionAsset: '質問するか @で指定',
    main: 'メイン',
    mainAsset: 'メインアセット',
    generatingImage: '画像を生成中',
    productListing: '商品リスティング',
    socialMedia: 'ソーシャルメディア',
    lifestyle: 'ライフスタイル',
    detail: '詳細',
    layoutView: 'レイアウト表示',
    groupedView: 'グループ表示',
    allAssetsView: 'すべてのアセット',
    listView: 'リスト表示',
    exitSelectionMode: '選択モードを終了',
    selecting: '選択中',
    selectAssets: 'アセットを選択',
    select: '選択',
    selectedCount: '{count} 件を選択中',
    clearSelection: '選択を解除',
    selectAll: 'すべて選択',
    clear: 'クリア',
    allCount: 'すべて ({count})',
    downloadSelected: '選択項目をダウンロード',
    preparing: '準備中...',
    download: 'ダウンロード',
    mentionSelectedInChat: '選択項目をチャットでメンション',
    mention: 'メンション',
    deleteSelected: '選択項目を削除',
    delete: '削除',
    editAsset: 'アセットを編集',
    viewFullScreen: '全画面で表示',
    deleteAsset: 'アセットを削除',
    deleteThisAsset: 'このアセットを削除しますか？',
    selectAsset: 'アセットを選択',
    openGenerationChat: '生成チャットを開く',
    chooseAssetToDiscuss: 'チャットで話したいアセットを選択してください',
    mainAssetAlt: '{productName} のメインアセット',
    version: 'バージョン',
    noVersionSelected: 'バージョンが選択されていません',
    noMainAsset: 'メインアセットがありません',
    selected: '選択中',
    newLabel: '新規',
    moreVersionsAvailable: 'あと{count}件のバージョンがあります',
    selectedAsset: '選択中のアセット',
    videoAsset: '動画アセット',
    threeDModel: '3Dモデル',
    replaceAsset: 'アセットを置き換える',
    maximizeAsset: 'アセットを最大化',
    minimizeAsset: 'アセットを最小化',
    assetPreview: 'アセットのプレビュー',
    imageLabel: '画像',
    openAsset: 'アセットを開く',
    selectFileForDownload: 'ダウンロードするファイルを選択',
    deselectFile: 'ファイルの選択を解除',
    createdRelative: '{time}に作成',
    updatedRelative: '{time}に更新',
    generatingVideo: '動画を生成中',
    fineTuningImage: '画像を微調整中',
    changeSelectionWhilePinned:
      'アセットが固定されている間は選択を変更できません。先に固定を解除してください。',
    selectVersionBeforePreparingDownload:
      'ダウンロードを準備する前にバージョンを選択してください。',
    selectAtLeastOneFile: '少なくとも1つのファイルを選択してください。',
    preparingFilesForDownload:
      '{count}件のファイルをダウンロード用に準備しています',
    addedAssetsToChat: 'アセットを{count}件チャットに追加しました',
    deleteAssetsTitle: 'アセットを削除しますか？',
    deleteAssetsDescription:
      'この操作は取り消せません。選択したアセットは完全に削除されます。',
    deletingAssets: '{count}件のアセットを削除中...',
    deletedAssets: '{count}件のアセットを削除しました',
    somethingWentWrong: '問題が発生しました',
    changeLayoutView: 'レイアウト表示を変更',
    deleteAssetQuestion: 'このアセットを削除しますか？',
    deletingAsset: 'アセットを削除中...',
    assetDeleted: 'アセットを削除しました',
    generatingAdditionalAssets: '追加のアセットを生成中...',
    creatingMoreAssets: 'この商品の追加アセットを作成しています',
    generatingMainAsset: 'メインアセットを生成中...',

    logoReference: {
      title: 'ロゴリファレンス',
      description:
        '再利用可能なロゴリファレンスをここに保存しておくと、今後の生成で適切なマーク、配置のヒント、カラー処理を維持できます。',
      autoBadge: '自動',
      manualBadge: '手動',
      addManual: 'ロゴをアップロード',
      editManual: 'ロゴをアップロード',
      reextract: '抽出を再実行',
      processing: 'ロゴリファレンスを生成中。',
      noSavedReference: '保存されたロゴリファレンスはまだありません。',
      saveFailed: 'ロゴリファレンスの保存に失敗しました。',
      uploadFailed: 'ロゴリファレンスのアップロードに失敗しました。',
      logoTextLabel: 'ロゴテキスト',
      logoTextPlaceholder: 'ブランドのワードマークまたはロゴテキスト',
      contextLabel: 'コンテキスト',
      contextPlaceholder:
        '例：リブ編みの黒ニットの上、左胸上部に小さなトーン・オン・トーンのクリーム刺繍',
    },
  },
  avatarSelector: {
    failedToSaveAvatarSelection: 'アバター選択を保存できませんでした',
    failedToRemoveAvatar: 'アバターを削除できませんでした',
    publishedIndividually: '個別に公開',
    createAvatarCharacterPrompt: 'アバターキャラクターを作成: {seed}',
    addAvatar: 'アバターを追加',
    editAvatars: 'アバターを編集',
    createAvatars: 'アバターを作成',
    createAvatarsDescription:
      'アバターを使うと、一貫したキャラクターでライフスタイル画像やSNS画像を生成できます。',
    goToDesign: 'デザインへ移動',
    useAvatars: 'アバターを使う',
    useAvatarsDescription:
      'ライフスタイル画像やSNS画像の生成に使うアバターを最大5つ選んでください。',
    on: 'オン',
    off: 'オフ',
    avatarsEnabledDescription:
      '選択したアバターはライフスタイル画像やSNS画像に使用されます。',
    avatarsDisabledDescription:
      'アバターは無効です。生成に使うにはオンにしてください。',
    selectAvatars: 'アバターを選択',
    selectAvatarsDescription:
      'ライフスタイル画像やSNS画像に使うアバターを、ワークスペースまたは公開セットから最大5つ選択してください。',
    searchAvatars: 'アバターを検索...',
    noAvatarsFound: 'アバターが見つかりません',
    tryDifferentSearchTerm: '別の検索語を試してください',
    noAvatarsAvailable: '利用可能なアバターがありません',
    createAvatarsFirst: 'まずアバターセクションでアバターを作成してください。',
    yourWorkspace: 'あなたのワークスペースのアバター',
    sharedWithYou: '共有アバター',
    privateAndPublished: '非公開と公開済み',
    private: '非公開アバター',
    publishedSets: '公開セット',
    verifiedPublishers: '認証済みパブリッシャー',
    avatarCount: '{count} 件のアバター',
    selected: '選択済み:',
    cancel: 'キャンセル',
    save: '保存',
    noChanges: '変更なし',
    publishedByWorkspace: '{workspaceName} により公開',
    verifiedWorkspace: '認証済みワークスペース',
    failed: '失敗',
    queued: 'キュー済み',
    generating: '生成中',
    removeAvatar: '{name} を削除',
    avatarSeedPrompts: AVATAR_SEED_PROMPTS,
  },
  generationSuggestions: {
    generatingAssets: 'アセットを生成中',
    starting: '開始中...',
    stoppedByUser: 'ユーザーにより停止',
    regenerateSuggestions: '提案を再生成',
    regenerateSuggestionsDescription:
      '現在のメイン画像とギャラリーをもとに商品を再分析し、新しい提案を作成します。既存の提案は置き換えられます。',
    regenerate: '再生成',
    cancel: 'キャンセル',
    runAllSuggestions: 'すべての提案を実行',
    runAllSuggestionsDescription:
      'すべての提案を実行します（合計 {totalImages} 枚）。少し時間がかかる場合がありますが、完了したものから順に表示されます。',
    fullAutoMode: 'フルオートモード',
    fullAutoModeDescription:
      '現在のメイン画像と既存のギャラリーをもとに、次に生成する内容を自動で判断し、すぐに生成を開始します。',
    startFullAutoMode: 'フルオートモードを開始',
    imageSingular: '画像',
    imagePlural: '画像',
    numberOfImagesToGenerate: '生成する画像数',
    generateBetween: '1 から {count} {label} を生成',
    editSuggestion: '提案を編集',
    maxImagesSavedForSuggestion: 'この提案に保存される最大画像数',
    savesDefaultMaxForFutureRuns:
      'この提案を今後実行するときのデフォルト最大数を保存します。',
    saveDefaults: 'デフォルトを保存',
    aiSuggestions: 'AI提案',
    generatingImageCount: '{count} 枚の画像を生成中...',
    stopGeneration: '生成を停止',
    runAll: 'すべて実行',
    fullAuto: 'フルオート',
    analyzingProductAndGeneratingSuggestions:
      '商品を分析して提案を生成しています...',
    noSuggestionsYet: 'まだ提案はありません',
    noSuggestionsDescription:
      'より良いアセット作成に役立つAI提案を生成しましょう。',
    generateSuggestions: '提案を生成',
    failedToLoadSuggestions:
      '提案を読み込めませんでした。もう一度お試しください。',
    generateImageCountCta: '{count} {label} を生成',
  },
  walkthrough: {
    productSuggestionsTitle: 'アセットを生成',
    productSuggestionsDescription:
      '商品リスティング、ライフスタイル画像、SNS広告、UGC など、さまざまな用途向けのアセットをワンクリックで生成できます。タイプを選ぶと IMAI がすぐにバッチ生成します。',
    productAvatarsTitle: 'アバターを作成または使用',
    productAvatarsDescription:
      'この商品に一貫したキャラクターワークフローを作れます。まだアバターがない場合は Design に移動して作成してください。ある場合はここで最大5つ選び、ライフスタイル画像やSNS画像の生成に使えます。',
    productTabsTitle: '作業サーフェスを選ぶ',
    productTabsDescription:
      'これらのタブは出力を整理するだけでなく、商品を異なる作業モードに切り替えます。Media Studio はビジュアル作成、Catalogue Data はEC向け商品情報、Prints and Patterns は反復的なデザイン探索、tech-pack は生産ワークフロー向けです。',
    productGalleryTitle: 'アセットギャラリー',
    productGalleryDescription:
      '生成した画像、動画、3Dモデルはすべてここに並びます。アセットにカーソルを合わせると、ダウンロード、アップスケール、インペインティング編集、またはチャットでの参照ができます。',
    productAddAssetTitle: 'アセットを追加または生成',
    productAddAssetDescription:
      '自分の写真や動画をアップロードすることも、このグループ用に新しいアセットを生成することもできます。各アセットグループは、キャンペーン、スタイル、用途ごとにコンテンツを整理します。',
    productChatTabsTitle: 'チャットワークスペース',
    productChatTabsDescription:
      'これは汎用チャットではなく、商品専用のワークスペースです。アクティブなスレッドは1つの考え方に集中でき、Chats ではこの商品とバージョンに紐づく保存済み会話を行き来できます。',
    productChatNewTitle: '新しい方向へ分岐',
    productChatNewDescription:
      '現在の会話を上書きせずに、新しい探索を始めるには New chat を使います。異なるクリエイティブや戦略の方向性を並べて比較する一番速い方法です。',
    productChatInputTitle: 'メンションとコンテキストを使う',
    productChatInputDescription:
      'この入力欄は現在の商品、バージョン、作業サーフェスを理解しています。アセット、グループ、参考資料をチャット内で直接メンションすることで、IMAI はテキストだけでなく正確な文脈をもとに動けます。',
    productVersionsTitle: 'バージョン履歴',
    productVersionsDescription:
      '各バージョンは商品のスナップショットです。以前の作業を失うことなく、別の方向性を試すために新しいバージョンを作成できます。',
    mobileProductTabsTitle: '作業サーフェスを選ぶ',
    mobileProductTabsDescription:
      'これらのタブをスワイプして、商品の作業モードを切り替えます。Media Studio はビジュアル、Catalogue Data は商品一覧、Prints and Patterns は反復的なデザイン探索、tech-pack は生産ワークフロー向けです。',
    mobileProductChatTitle: '商品ワークスペースチャット',
    mobileProductChatDescription:
      'ここをタップすると、この商品と今見ているサーフェスに紐づいたチャットが開きます。バリエーション、スタイル変更、次のステップを依頼すると、IMAI が商品コンテキスト付きで応答します。',
  },
};

const KO_COPY: ProductStaticCopy = {
  workspace: {
    mediaStudio: '미디어 스튜디오',
    catalogueData: '카탈로그 데이터',
    printsAndPatterns: '프린트 및 패턴',
    technicalBlueprints: 'Technical Studio',
    manufacturing: '제조',
    threeDModels: '3D 모델',
    creating: '생성 중...',
    newChat: '새 채팅',
    chatWithThisProduct: '이 제품과 채팅',
    newTab: '새 탭',
    chats: '채팅',
    failedToProcessMessage: '메시지를 처리하지 못했습니다',
    askAnythingOrMentionAsset: '질문하거나 @멘션',
    main: '메인',
    mainAsset: '메인 에셋',
    generatingImage: '이미지 생성 중',
    productListing: '상품 리스트',
    socialMedia: '소셜 미디어',
    lifestyle: '라이프스타일',
    detail: '디테일',
    layoutView: '레이아웃 보기',
    groupedView: '그룹 보기',
    allAssetsView: '전체 에셋 보기',
    listView: '목록 보기',
    exitSelectionMode: '선택 모드 종료',
    selecting: '선택 중',
    selectAssets: '에셋 선택',
    select: '선택',
    selectedCount: '{count}개 선택됨',
    clearSelection: '선택 해제',
    selectAll: '전체 선택',
    clear: '지우기',
    allCount: '전체 ({count})',
    downloadSelected: '선택 항목 다운로드',
    preparing: '준비 중...',
    download: '다운로드',
    mentionSelectedInChat: '선택 항목을 채팅에 언급',
    mention: '언급',
    deleteSelected: '선택 항목 삭제',
    delete: '삭제',
    editAsset: '에셋 편집',
    viewFullScreen: '전체 화면으로 보기',
    deleteAsset: '에셋 삭제',
    deleteThisAsset: '이 에셋을 삭제할까요?',
    selectAsset: '에셋 선택',
    openGenerationChat: '생성 채팅 열기',
    chooseAssetToDiscuss: '채팅에서 이야기할 에셋을 선택하세요',
    mainAssetAlt: '{productName} 메인 에셋',
    version: '버전',
    noVersionSelected: '선택된 버전이 없습니다',
    noMainAsset: '메인 에셋이 없습니다',
    selected: '선택됨',
    newLabel: '새로',
    moreVersionsAvailable: '추가 버전 {count}개 사용 가능',
    selectedAsset: '선택된 에셋',
    videoAsset: '비디오 에셋',
    threeDModel: '3D 모델',
    replaceAsset: '에셋 교체',
    maximizeAsset: '에셋 최대화',
    minimizeAsset: '에셋 최소화',
    assetPreview: '에셋 미리보기',
    imageLabel: '이미지',
    openAsset: '에셋 열기',
    selectFileForDownload: '다운로드할 파일 선택',
    deselectFile: '파일 선택 해제',
    createdRelative: '{time}에 생성됨',
    updatedRelative: '{time}에 업데이트됨',
    generatingVideo: '비디오 생성 중',
    fineTuningImage: '이미지 미세 조정 중',
    changeSelectionWhilePinned:
      '에셋이 고정된 동안에는 선택을 변경할 수 없습니다. 먼저 고정을 해제하세요.',
    selectVersionBeforePreparingDownload:
      '다운로드를 준비하기 전에 버전을 선택하세요.',
    selectAtLeastOneFile: '파일을 하나 이상 선택하세요.',
    preparingFilesForDownload: '다운로드할 파일 {count}개 준비 중',
    addedAssetsToChat: '에셋 {count}개를 채팅에 추가했습니다',
    deleteAssetsTitle: '에셋을 삭제할까요?',
    deleteAssetsDescription:
      '이 작업은 되돌릴 수 없습니다. 선택한 에셋이 영구적으로 삭제됩니다.',
    deletingAssets: '에셋 {count}개 삭제 중...',
    deletedAssets: '에셋 {count}개 삭제됨',
    somethingWentWrong: '문제가 발생했습니다',
    changeLayoutView: '레이아웃 보기 변경',
    deleteAssetQuestion: '이 에셋을 삭제할까요?',
    deletingAsset: '에셋 삭제 중...',
    assetDeleted: '에셋이 삭제되었습니다',
    generatingAdditionalAssets: '추가 에셋 생성 중...',
    creatingMoreAssets: '제품에 대한 더 많은 에셋을 생성하고 있습니다',
    generatingMainAsset: '메인 에셋 생성 중...',

    logoReference: {
      title: '로고 레퍼런스',
      description:
        '재사용 가능한 로고 레퍼런스를 여기에 저장해 두면, 다음 생성에서 올바른 마크, 배치 가이드, 컬러 처리를 유지할 수 있습니다.',
      autoBadge: '자동',
      manualBadge: '수동',
      addManual: '로고 업로드',
      editManual: '로고 업로드',
      reextract: '추출 다시 실행',
      processing: '로고 레퍼런스를 생성 중입니다.',
      noSavedReference: '저장된 로고 레퍼런스가 아직 없습니다.',
      saveFailed: '로고 레퍼런스 저장에 실패했습니다.',
      uploadFailed: '로고 레퍼런스 업로드에 실패했습니다.',
      logoTextLabel: '로고 텍스트',
      logoTextPlaceholder: '브랜드 워드마크 또는 로고 텍스트',
      contextLabel: '컨텍스트',
      contextPlaceholder:
        '예: 골지 검정 니트 위, 왼쪽 가슴 상단의 작은 톤온톤 크림 자수',
    },
  },
  avatarSelector: {
    failedToSaveAvatarSelection: '아바타 선택을 저장하지 못했습니다',
    failedToRemoveAvatar: '아바타를 제거하지 못했습니다',
    publishedIndividually: '개별 게시됨',
    createAvatarCharacterPrompt: '아바타 캐릭터 만들기: {seed}',
    addAvatar: '아바타 추가',
    editAvatars: '아바타 편집',
    createAvatars: '아바타 만들기',
    createAvatarsDescription:
      '아바타를 사용하면 일관된 캐릭터로 라이프스타일 및 소셜 이미지를 만들 수 있습니다.',
    goToDesign: '디자인으로 이동',
    useAvatars: '아바타 사용',
    useAvatarsDescription:
      '라이프스타일 및 소셜 이미지 생성을 위해 최대 5개의 아바타를 선택하세요.',
    on: '켜짐',
    off: '꺼짐',
    avatarsEnabledDescription:
      '선택한 아바타가 라이프스타일 및 소셜 이미지에 사용됩니다.',
    avatarsDisabledDescription:
      '아바타가 꺼져 있습니다. 생성에 사용하려면 켜세요.',
    selectAvatars: '아바타 선택',
    selectAvatarsDescription:
      '라이프스타일 및 소셜 이미지에 사용할 아바타를 워크스페이스나 게시된 세트에서 최대 5개까지 선택하세요.',
    searchAvatars: '아바타 검색...',
    noAvatarsFound: '아바타를 찾을 수 없습니다',
    tryDifferentSearchTerm: '다른 검색어를 시도해 보세요',
    noAvatarsAvailable: '사용 가능한 아바타가 없습니다',
    createAvatarsFirst: '먼저 아바타 섹션에서 아바타를 만들어 주세요.',
    yourWorkspace: '내 워크스페이스 아바타',
    sharedWithYou: '공유된 아바타',
    privateAndPublished: '비공개 및 게시됨',
    private: '비공개 아바타',
    publishedSets: '게시된 세트',
    verifiedPublishers: '인증된 게시자',
    avatarCount: '{count}개 아바타',
    selected: '선택됨:',
    cancel: '취소',
    save: '저장',
    noChanges: '변경 사항 없음',
    publishedByWorkspace: '{workspaceName}에서 게시',
    verifiedWorkspace: '인증된 워크스페이스',
    failed: '실패',
    queued: '대기 중',
    generating: '생성 중',
    removeAvatar: '{name} 제거',
    avatarSeedPrompts: AVATAR_SEED_PROMPTS,
  },
  generationSuggestions: {
    generatingAssets: '에셋 생성 중',
    starting: '시작 중...',
    stoppedByUser: '사용자가 중지함',
    regenerateSuggestions: '제안 다시 생성',
    regenerateSuggestionsDescription:
      '현재 메인 이미지와 갤러리를 기준으로 제품을 다시 분석하고 새로운 제안을 생성합니다. 기존 제안은 교체됩니다.',
    regenerate: '다시 생성',
    cancel: '취소',
    runAllSuggestions: '모든 제안 실행',
    runAllSuggestionsDescription:
      '모든 제안을 실행합니다(총 {totalImages}장). 시간이 조금 걸릴 수 있지만, 완료되는 대로 에셋이 차례로 나타납니다.',
    fullAutoMode: '풀 오토 모드',
    fullAutoModeDescription:
      '현재 메인 이미지와 기존 갤러리를 바탕으로 다음에 무엇을 생성할지 자동으로 결정한 뒤 즉시 시작합니다.',
    startFullAutoMode: '풀 오토 모드 시작',
    imageSingular: '이미지',
    imagePlural: '이미지',
    numberOfImagesToGenerate: '생성할 이미지 수',
    generateBetween: '1개에서 {count}개 {label} 사이로 생성',
    editSuggestion: '제안 편집',
    maxImagesSavedForSuggestion: '이 제안에 저장할 최대 이미지 수',
    savesDefaultMaxForFutureRuns:
      '이 제안의 향후 실행에 사용할 기본 최대값을 저장합니다.',
    saveDefaults: '기본값 저장',
    aiSuggestions: 'AI 제안',
    generatingImageCount: '{count}개 이미지 생성 중...',
    stopGeneration: '생성 중지',
    runAll: '모두 실행',
    fullAuto: '풀 오토',
    analyzingProductAndGeneratingSuggestions:
      '제품을 분석하고 제안을 생성하는 중...',
    noSuggestionsYet: '아직 제안이 없습니다',
    noSuggestionsDescription:
      '더 나은 에셋 생성을 돕는 AI 제안을 생성해 보세요.',
    generateSuggestions: '제안 생성',
    failedToLoadSuggestions: '제안을 불러오지 못했습니다. 다시 시도해 주세요.',
    generateImageCountCta: '{count}개 {label} 생성',
  },
  walkthrough: {
    productSuggestionsTitle: '에셋 생성하기',
    productSuggestionsDescription:
      '상품 리스트, 라이프스타일 컷, 소셜 광고, UGC 등 다양한 용도의 에셋을 한 번에 생성할 수 있습니다. 유형을 고르면 IMAI가 즉시 배치를 만들어 줍니다.',
    productAvatarsTitle: '아바타 만들기 또는 사용하기',
    productAvatarsDescription:
      '이 제품을 위한 일관된 캐릭터 워크플로를 만들 수 있습니다. 아직 아바타가 없다면 Design으로 이동해 만들고, 있다면 여기서 최대 5개를 골라 라이프스타일 및 소셜 생성에 사용하세요.',
    productTabsTitle: '작업 화면 선택하기',
    productTabsDescription:
      '이 탭들은 결과를 정리하는 것 이상으로 제품을 서로 다른 작업 모드로 전환합니다. 미디어 스튜디오는 비주얼 제작, 카탈로그 데이터는 커머스용 상품 정보, 프린트 및 패턴은 반복 가능한 디자인 탐색, 기술 블루프린트는 생산 워크플로에 사용됩니다.',
    productGalleryTitle: '에셋 갤러리',
    productGalleryDescription:
      '생성된 이미지, 영상, 3D 모델이 모두 여기에 모입니다. 에셋 위에 마우스를 올리면 다운로드, 업스케일, 인페인팅 편집, 또는 채팅에서의 언급으로 추가 개선을 진행할 수 있습니다.',
    productAddAssetTitle: '에셋 추가 또는 생성',
    productAddAssetDescription:
      '직접 찍은 사진과 영상을 업로드하거나, 이 그룹을 위한 새 에셋을 생성하세요. 각 에셋 그룹은 캠페인, 스타일, 활용 목적에 따라 콘텐츠를 정리합니다.',
    productChatTabsTitle: '채팅 작업공간',
    productChatTabsDescription:
      '이곳은 일반 채팅이 아니라 제품 전용 작업공간입니다. 현재 스레드는 한 방향의 사고를 유지하고, Chats에서는 이 제품과 버전에 저장된 대화를 오갈 수 있습니다.',
    productChatNewTitle: '새로운 방향으로 가지치기',
    productChatNewDescription:
      '기존 대화를 덮어쓰지 않고 새로운 탐색을 시작하려면 New chat을 사용하세요. 서로 다른 크리에이티브 또는 전략 방향을 나란히 비교하는 가장 빠른 방법입니다.',
    productChatInputTitle: '멘션과 컨텍스트 사용하기',
    productChatInputDescription:
      '이 입력창은 현재 제품, 버전, 작업 화면을 이해합니다. 에셋, 그룹, 참고 자료를 채팅에 직접 멘션하면 IMAI가 텍스트만이 아니라 정확한 맥락으로 작업할 수 있습니다.',
    productVersionsTitle: '버전 기록',
    productVersionsDescription:
      '각 버전은 제품의 스냅샷을 저장합니다. 이전 작업을 잃지 않고 다른 방향을 탐색하려면 새 버전을 만드세요.',
    mobileProductTabsTitle: '작업 화면 선택하기',
    mobileProductTabsDescription:
      '이 탭들을 스와이프해 제품을 다른 작업 모드로 전환하세요. 미디어 스튜디오는 비주얼, 카탈로그 데이터는 리스트, 프린트 및 패턴은 반복 가능한 디자인 탐색, 기술 블루프린트는 생산 워크플로에 사용됩니다.',
    mobileProductChatTitle: '제품 작업공간 채팅',
    mobileProductChatDescription:
      '탭하여 현재 보고 있는 화면과 연결된 제품 채팅을 여세요. 변형, 스타일 변경, 다음 단계를 요청하면 IMAI가 제품 맥락에 맞게 응답합니다.',
  },
};

const RU_COPY: ProductStaticCopy = {
  workspace: {
    mediaStudio: 'Медиа-студия',
    catalogueData: 'Данные каталога',
    printsAndPatterns: 'Принты и узоры',
    technicalBlueprints: 'Technical Studio',
    manufacturing: 'Производство',
    threeDModels: '3D-модели',
    creating: 'Создание...',
    newChat: 'Новый чат',
    chatWithThisProduct: 'Обсудить этот продукт',
    newTab: 'Новая вкладка',
    chats: 'Чаты',
    failedToProcessMessage: 'Не удалось обработать сообщение',
    askAnythingOrMentionAsset: 'Спросите или @упомяните',
    main: 'Основной',
    mainAsset: 'Основной ресурс',
    generatingImage: 'Генерация изображения',
    productListing: 'Карточка товара',
    socialMedia: 'Социальные сети',
    lifestyle: 'Лайфстайл',
    detail: 'Деталь',
    layoutView: 'Вид макета',
    groupedView: 'Сгруппированный вид',
    allAssetsView: 'Все ресурсы',
    listView: 'Список',
    exitSelectionMode: 'Выйти из режима выбора',
    selecting: 'Выбор',
    selectAssets: 'Выбрать ресурсы',
    select: 'Выбрать',
    selectedCount: 'Выбрано: {count}',
    clearSelection: 'Снять выделение',
    selectAll: 'Выбрать всё',
    clear: 'Очистить',
    allCount: 'Все ({count})',
    downloadSelected: 'Скачать выбранное',
    preparing: 'Подготовка...',
    download: 'Скачать',
    mentionSelectedInChat: 'Упомянуть выбранное в чате',
    mention: 'Упомянуть',
    deleteSelected: 'Удалить выбранное',
    delete: 'Удалить',
    editAsset: 'Редактировать ресурс',
    viewFullScreen: 'Открыть на весь экран',
    deleteAsset: 'Удалить ресурс',
    deleteThisAsset: 'Удалить этот ресурс?',
    selectAsset: 'Выбрать ресурс',
    openGenerationChat: 'Открыть чат генерации',
    chooseAssetToDiscuss: 'Выберите ресурс для обсуждения в чате',
    mainAssetAlt: 'основной ресурс {productName}',
    version: 'Версия',
    noVersionSelected: 'Версия не выбрана',
    noMainAsset: 'Нет основного ресурса',
    selected: 'Выбрано',
    newLabel: 'Новый',
    moreVersionsAvailable: 'Доступно еще {count} версий',
    selectedAsset: 'Выбранный ресурс',
    videoAsset: 'Видео-ресурс',
    threeDModel: '3D-модель',
    replaceAsset: 'Заменить ресурс',
    maximizeAsset: 'Развернуть ресурс',
    minimizeAsset: 'Свернуть ресурс',
    assetPreview: 'Предпросмотр ресурса',
    imageLabel: 'Изображение',
    openAsset: 'Открыть ресурс',
    selectFileForDownload: 'Выбрать файл для загрузки',
    deselectFile: 'Снять выбор файла',
    createdRelative: 'Создано {time}',
    updatedRelative: 'Обновлено {time}',
    generatingVideo: 'Генерация видео',
    fineTuningImage: 'Тонкая настройка изображения',
    changeSelectionWhilePinned:
      'Нельзя изменить выбор, пока ресурс закреплен. Сначала открепите его.',
    selectVersionBeforePreparingDownload:
      'Выберите версию перед подготовкой загрузки.',
    selectAtLeastOneFile: 'Выберите хотя бы один файл.',
    preparingFilesForDownload: 'Подготавливаем {count} файл(ов) к загрузке',
    addedAssetsToChat: 'Добавлено {count} ресурс(ов) в чат',
    deleteAssetsTitle: 'Удалить ресурсы?',
    deleteAssetsDescription:
      'Это действие нельзя отменить. Выбранные ресурсы будут удалены навсегда.',
    deletingAssets: 'Удаляем {count} ресурс(ов)...',
    deletedAssets: 'Удалено {count} ресурс(ов)',
    somethingWentWrong: 'Что-то пошло не так',
    changeLayoutView: 'Изменить вид макета',
    deleteAssetQuestion: 'Удалить этот ресурс?',
    deletingAsset: 'Удаляем ресурс...',
    assetDeleted: 'Ресурс удален',
    generatingAdditionalAssets: 'Генерируются дополнительные ресурсы...',
    creatingMoreAssets: 'Создаем больше ресурсов для вашего продукта',
    generatingMainAsset: 'Генерируется основной ресурс...',

    logoReference: {
      title: 'Референс логотипа',
      description:
        'Сохраните здесь многоразовый референс логотипа, чтобы будущие генерации сохраняли правильный знак, подсказки по размещению и цветовое решение.',
      autoBadge: 'Авто',
      manualBadge: 'Ручной',
      addManual: 'Загрузите логотип',
      editManual: 'Загрузите логотип',
      reextract: 'Перезапустить извлечение',
      processing: 'Создаём референс логотипа.',
      noSavedReference: 'Сохранённого референса логотипа пока нет.',
      saveFailed: 'Не удалось сохранить референс логотипа.',
      uploadFailed: 'Не удалось загрузить референс логотипа.',
      logoTextLabel: 'Текст логотипа',
      logoTextPlaceholder: 'Wordmark бренда или текст логотипа',
      contextLabel: 'Контекст',
      contextPlaceholder:
        'Пример: небольшая тон-в-тон кремовая вышивка слева вверху на груди поверх чёрной кашкорсе',
    },
  },
  avatarSelector: {
    failedToSaveAvatarSelection: 'Не удалось сохранить выбор аватаров',
    failedToRemoveAvatar: 'Не удалось удалить аватар',
    publishedIndividually: 'Опубликовано отдельно',
    createAvatarCharacterPrompt: 'Создать персонажа-аватара: {seed}',
    addAvatar: 'Добавить аватар',
    editAvatars: 'Редактировать аватары',
    createAvatars: 'Создать аватары',
    createAvatarsDescription:
      'Аватары помогают создавать lifestyle- и social-сцены с единым персонажем.',
    goToDesign: 'Перейти в Design',
    useAvatars: 'Использовать аватары',
    useAvatarsDescription:
      'Выберите до 5 аватаров для генерации lifestyle- и social-изображений.',
    on: 'Вкл.',
    off: 'Выкл.',
    avatarsEnabledDescription:
      'Выбранные аватары будут использоваться в lifestyle- и social-сценах.',
    avatarsDisabledDescription:
      'Аватары отключены. Включите их, чтобы использовать в генерациях.',
    selectAvatars: 'Выбрать аватары',
    selectAvatarsDescription:
      'Выберите до 5 аватаров из рабочего пространства или опубликованных наборов для lifestyle- и social-сцен.',
    searchAvatars: 'Поиск аватаров...',
    noAvatarsFound: 'Аватары не найдены',
    tryDifferentSearchTerm: 'Попробуйте другой поисковый запрос',
    noAvatarsAvailable: 'Нет доступных аватаров',
    createAvatarsFirst: 'Сначала создайте аватары в разделе Avatars.',
    yourWorkspace: 'Аватары вашего рабочего пространства',
    sharedWithYou: 'Аватары, которыми поделились с вами',
    privateAndPublished: 'Личные и опубликованные',
    private: 'Личные аватары',
    publishedSets: 'Опубликованные наборы',
    verifiedPublishers: 'Проверенные издатели',
    avatarCount: '{count} аватаров',
    selected: 'Выбрано:',
    cancel: 'Отмена',
    save: 'Сохранить',
    noChanges: 'Без изменений',
    publishedByWorkspace: 'Опубликовано {workspaceName}',
    verifiedWorkspace: 'Проверенное рабочее пространство',
    failed: 'Сбой',
    queued: 'В очереди',
    generating: 'Генерация',
    removeAvatar: 'Удалить {name}',
    avatarSeedPrompts: AVATAR_SEED_PROMPTS,
  },
  generationSuggestions: {
    generatingAssets: 'Генерация ресурсов',
    starting: 'Запуск...',
    stoppedByUser: 'Остановлено пользователем',
    regenerateSuggestions: 'Пересоздать предложения',
    regenerateSuggestionsDescription:
      'Продукт будет снова проанализирован, и на основе текущего главного изображения и галереи появятся новые предложения. Текущие предложения будут заменены.',
    regenerate: 'Пересоздать',
    cancel: 'Отмена',
    runAllSuggestions: 'Запустить все предложения',
    runAllSuggestionsDescription:
      'Будут запущены все предложения (всего {totalImages} изображений). Это может занять некоторое время, но ресурсы будут появляться по мере готовности.',
    fullAutoMode: 'Полностью автоматический режим',
    fullAutoModeDescription:
      'Система автоматически решит, что генерировать дальше на основе текущего главного изображения и галереи, и сразу начнёт работу.',
    startFullAutoMode: 'Запустить авто-режим',
    imageSingular: 'изображение',
    imagePlural: 'изображения',
    numberOfImagesToGenerate: 'Количество генерируемых изображений',
    generateBetween: 'Сгенерировать от 1 до {count} {label}',
    editSuggestion: 'Редактировать предложение',
    maxImagesSavedForSuggestion:
      'Максимум изображений, сохраняемый для этого предложения',
    savesDefaultMaxForFutureRuns:
      'Сохраняет максимальное значение по умолчанию для будущих запусков этого предложения.',
    saveDefaults: 'Сохранить по умолчанию',
    aiSuggestions: 'AI-предложения',
    generatingImageCount: 'Генерация {count} изображений...',
    stopGeneration: 'Остановить генерацию',
    runAll: 'Запустить всё',
    fullAuto: 'Авто',
    analyzingProductAndGeneratingSuggestions:
      'Анализируем продукт и создаём предложения...',
    noSuggestionsYet: 'Пока нет предложений',
    noSuggestionsDescription:
      'Создайте предложения на базе ИИ, чтобы делать более сильные ресурсы.',
    generateSuggestions: 'Создать предложения',
    failedToLoadSuggestions:
      'Не удалось загрузить предложения. Попробуйте ещё раз.',
    generateImageCountCta: 'Сгенерировать {count} {label}',
  },
  walkthrough: {
    productSuggestionsTitle: 'Генерировать ресурсы',
    productSuggestionsDescription:
      'Создавайте ресурсы в один клик для разных задач: карточек товара, lifestyle-кадров, соцрекламы и UGC. Выберите тип, и IMAI сразу сгенерирует пакет.',
    productAvatarsTitle: 'Создайте аватары или используйте их',
    productAvatarsDescription:
      'Постройте последовательный персонажный workflow для этого продукта. Если аватаров ещё нет, перейдите в Design и создайте их. Если есть, выберите здесь до 5 для lifestyle- и social-генераций.',
    productTabsTitle: 'Выберите рабочую поверхность',
    productTabsDescription:
      'Эти вкладки не просто организуют результаты, а переключают продукт между разными режимами работы. Медиа-студия нужна для визуалов, Данные каталога для e-commerce информации, Принты и узоры для повторяемых дизайн-исследований, а Технические чертежи для производственных workflow.',
    productGalleryTitle: 'Галерея ресурсов',
    productGalleryDescription:
      'Здесь хранятся все сгенерированные изображения, видео и 3D-модели. Наведите курсор на ресурс, чтобы скачать его, увеличить, отредактировать через inpainting или упомянуть в чате для дальнейшей доработки.',
    productAddAssetTitle: 'Добавить или сгенерировать ресурсы',
    productAddAssetDescription:
      'Загружайте свои фото и видео или генерируйте новые ресурсы для этой группы. Каждая группа организует контент по кампании, стилю или сценарию использования.',
    productChatTabsTitle: 'Рабочее пространство чата',
    productChatTabsDescription:
      'Это рабочее пространство, привязанное к продукту, а не обычный чат. Активный тред удерживает одну линию мысли, а Chats позволяет переключаться между сохранёнными разговорами для этого продукта и версии.',
    productChatNewTitle: 'Ответвляйтесь в новые направления',
    productChatNewDescription:
      'Используйте New chat, чтобы начать новое исследование, не перезаписывая текущий разговор. Это самый быстрый способ сравнить творческие или стратегические направления рядом друг с другом.',
    productChatInputTitle: 'Используйте упоминания и контекст',
    productChatInputDescription:
      'Этот ввод понимает текущий продукт, версию и рабочую поверхность. Упоминайте ресурсы, группы и ссылки прямо в чате, чтобы IMAI работал с точным контекстом, а не только с текстом.',
    productVersionsTitle: 'История версий',
    productVersionsDescription:
      'Каждая версия сохраняет снимок продукта. Создавайте новые версии, чтобы исследовать разные направления, не теряя предыдущую работу.',
    mobileProductTabsTitle: 'Выберите рабочую поверхность',
    mobileProductTabsDescription:
      'Проведите по этим вкладкам, чтобы переключать режимы работы продукта. Медиа-студия отвечает за визуалы, Данные каталога за листинги, Принты и узоры за повторяемые дизайн-исследования, а Технические чертежи за производство.',
    mobileProductChatTitle: 'Чат рабочего пространства продукта',
    mobileProductChatDescription:
      'Нажмите, чтобы открыть чат, привязанный к этому продукту и текущей поверхности. Запрашивайте вариации, смену стиля или следующие шаги, и IMAI ответит с учётом контекста продукта.',
  },
};

const ZH_COPY: ProductStaticCopy = {
  workspace: {
    mediaStudio: '媒体工作室',
    catalogueData: '目录数据',
    printsAndPatterns: '印花与图案',
    technicalBlueprints: 'Technical Studio',
    manufacturing: '生产制造',
    threeDModels: '3D 模型',
    creating: '创建中...',
    newChat: '新建聊天',
    chatWithThisProduct: '与此产品聊天',
    newTab: '新标签页',
    chats: '聊天',
    failedToProcessMessage: '处理消息失败',
    askAnythingOrMentionAsset: '提问或 @提及',
    main: '主图',
    mainAsset: '主素材',
    generatingImage: '正在生成图片',
    productListing: '产品展示图',
    socialMedia: '社交媒体',
    lifestyle: '生活方式',
    detail: '细节',
    layoutView: '布局视图',
    groupedView: '分组视图',
    allAssetsView: '全部素材视图',
    listView: '列表视图',
    exitSelectionMode: '退出选择模式',
    selecting: '选择中',
    selectAssets: '选择素材',
    select: '选择',
    selectedCount: '已选择 {count} 项',
    clearSelection: '清除选择',
    selectAll: '全选',
    clear: '清除',
    allCount: '全部（{count}）',
    downloadSelected: '下载所选项',
    preparing: '准备中...',
    download: '下载',
    mentionSelectedInChat: '在聊天中提及所选项',
    mention: '提及',
    deleteSelected: '删除所选项',
    delete: '删除',
    editAsset: '编辑素材',
    viewFullScreen: '全屏查看',
    deleteAsset: '删除素材',
    deleteThisAsset: '要删除这个素材吗？',
    selectAsset: '选择素材',
    openGenerationChat: '打开生成聊天',
    chooseAssetToDiscuss: '选择一个要在聊天中讨论的素材',
    mainAssetAlt: '{productName} 主素材',
    version: '版本',
    noVersionSelected: '未选择版本',
    noMainAsset: '没有主素材',
    selected: '已选中',
    newLabel: '新建',
    moreVersionsAvailable: '还有 {count} 个版本可用',
    selectedAsset: '已选素材',
    videoAsset: '视频素材',
    threeDModel: '3D 模型',
    replaceAsset: '替换素材',
    maximizeAsset: '最大化素材',
    minimizeAsset: '最小化素材',
    assetPreview: '素材预览',
    imageLabel: '图片',
    openAsset: '打开素材',
    selectFileForDownload: '选择要下载的文件',
    deselectFile: '取消选择文件',
    createdRelative: '创建于 {time}',
    updatedRelative: '更新于 {time}',
    generatingVideo: '正在生成视频',
    fineTuningImage: '正在微调图片',
    changeSelectionWhilePinned: '素材固定时无法更改选择。请先取消固定。',
    selectVersionBeforePreparingDownload: '准备下载前请先选择版本。',
    selectAtLeastOneFile: '请至少选择一个文件。',
    preparingFilesForDownload: '正在准备 {count} 个文件供下载',
    addedAssetsToChat: '已将 {count} 个素材添加到聊天',
    deleteAssetsTitle: '删除素材？',
    deleteAssetsDescription: '此操作无法撤销。所选素材将被永久删除。',
    deletingAssets: '正在删除 {count} 个素材...',
    deletedAssets: '已删除 {count} 个素材',
    somethingWentWrong: '出错了',
    changeLayoutView: '切换布局视图',
    deleteAssetQuestion: '要删除这个素材吗？',
    deletingAsset: '正在删除素材...',
    assetDeleted: '素材已删除',
    generatingAdditionalAssets: '正在生成更多素材...',
    creatingMoreAssets: '正在为你的产品生成更多素材',
    generatingMainAsset: '正在生成主素材...',

    logoReference: {
      title: 'Logo 参考',
      description:
        '将可复用的 Logo 参考保留在此，以便后续生成保留正确的标识、位置提示与色彩处理。',
      autoBadge: '自动',
      manualBadge: '手动',
      addManual: '上传你的 Logo',
      editManual: '上传你的 Logo',
      reextract: '重新运行提取',
      processing: '正在生成 Logo 参考。',
      noSavedReference: '尚未保存 Logo 参考。',
      saveFailed: '保存 Logo 参考失败。',
      uploadFailed: '上传 Logo 参考失败。',
      logoTextLabel: 'Logo 文本',
      logoTextPlaceholder: '品牌字标或 Logo 文本',
      contextLabel: '上下文',
      contextPlaceholder:
        '示例：在罗纹黑色针织上，左胸上方一处小巧的同色调奶油色刺绣',
    },
  },
  avatarSelector: {
    failedToSaveAvatarSelection: '保存头像选择失败',
    failedToRemoveAvatar: '移除头像失败',
    publishedIndividually: '单独发布',
    createAvatarCharacterPrompt: '创建一个头像角色：{seed}',
    addAvatar: '添加头像',
    editAvatars: '编辑头像',
    createAvatars: '创建头像',
    createAvatarsDescription:
      '头像可帮助你生成具有统一角色的生活方式和社交场景图片。',
    goToDesign: '前往设计',
    useAvatars: '使用头像',
    useAvatarsDescription:
      '最多选择 5 个头像，用于指导生活方式和社交图片生成。',
    on: '开',
    off: '关',
    avatarsEnabledDescription: '已选择的头像将用于生活方式和社交场景图片。',
    avatarsDisabledDescription: '头像已关闭。打开开关即可用于生成。',
    selectAvatars: '选择头像',
    selectAvatarsDescription:
      '从你的工作区或已发布头像集中最多选择 5 个头像，用于生活方式和社交场景图片。',
    searchAvatars: '搜索头像...',
    noAvatarsFound: '未找到头像',
    tryDifferentSearchTerm: '试试其他搜索词',
    noAvatarsAvailable: '暂无可用头像',
    createAvatarsFirst: '请先在“头像”页面创建头像。',
    yourWorkspace: '你的工作区头像',
    sharedWithYou: '共享头像',
    privateAndPublished: '私有和已发布',
    private: '私有头像',
    publishedSets: '已发布合集',
    verifiedPublishers: '已验证发布者',
    avatarCount: '{count} 个头像',
    selected: '已选择：',
    cancel: '取消',
    save: '保存',
    noChanges: '无更改',
    publishedByWorkspace: '由 {workspaceName} 发布',
    verifiedWorkspace: '已验证工作区',
    failed: '失败',
    queued: '已排队',
    generating: '生成中',
    removeAvatar: '移除 {name}',
    avatarSeedPrompts: AVATAR_SEED_PROMPTS,
  },
  generationSuggestions: {
    generatingAssets: '正在生成素材',
    starting: '开始中...',
    stoppedByUser: '已由用户停止',
    regenerateSuggestions: '重新生成建议',
    regenerateSuggestionsDescription:
      '这将重新分析你的产品，并根据当前主图和图库生成新的建议。现有建议将被替换。',
    regenerate: '重新生成',
    cancel: '取消',
    runAllSuggestions: '运行全部建议',
    runAllSuggestionsDescription:
      '这将运行全部建议（共 {totalImages} 张图片）。可能需要一些时间，但素材会在完成后逐步出现。',
    fullAutoMode: '全自动模式',
    fullAutoModeDescription:
      '系统将根据你产品的主图和现有图库自动决定下一步生成内容，并立即开始生成素材。',
    startFullAutoMode: '开始全自动模式',
    imageSingular: '图片',
    imagePlural: '图片',
    numberOfImagesToGenerate: '要生成的图片数量',
    generateBetween: '生成 1 到 {count} {label}',
    editSuggestion: '编辑建议',
    maxImagesSavedForSuggestion: '为该建议保存的最大图片数',
    savesDefaultMaxForFutureRuns: '保存该建议未来运行时的默认最大值。',
    saveDefaults: '保存默认值',
    aiSuggestions: 'AI 建议',
    generatingImageCount: '正在生成 {count} 张图片...',
    stopGeneration: '停止生成',
    runAll: '全部运行',
    fullAuto: '全自动',
    analyzingProductAndGeneratingSuggestions: '正在分析产品并生成建议...',
    noSuggestionsYet: '暂无建议',
    noSuggestionsDescription: '生成 AI 建议，帮助你创作更好的素材。',
    generateSuggestions: '生成建议',
    failedToLoadSuggestions: '加载建议失败。请重试。',
    generateImageCountCta: '生成 {count} {label}',
  },
  walkthrough: {
    productSuggestionsTitle: '生成素材',
    productSuggestionsDescription:
      '一键生成适用于不同场景的素材，从商品展示图、生活方式图片到社交广告和 UGC 内容都可以。选择一种类型，IMAI 会立即批量生成。',
    productAvatarsTitle: '创建或使用头像',
    productAvatarsDescription:
      '为这个产品建立统一的人物工作流。如果你还没有头像，可以跳转到设计页面创建；如果已经有了，就在这里最多选择 5 个，用于生活方式和社交场景生成。',
    productTabsTitle: '选择你的工作界面',
    productTabsDescription:
      '这些标签页不仅仅是整理输出，它们还会切换产品的不同工作模式。媒体工作室用于视觉素材创作，目录数据用于电商就绪的产品信息，印花与图案用于可重复设计探索，技术蓝图用于生产流程。',
    productGalleryTitle: '你的素材画廊',
    productGalleryDescription:
      '所有生成的图片、视频和 3D 模型都会出现在这里。将鼠标悬停在任意素材上，即可下载、放大、使用局部重绘编辑，或在聊天中提及它以继续细化。',
    productAddAssetTitle: '添加或生成素材',
    productAddAssetDescription:
      '上传你自己的照片和视频，或为这个分组生成新的素材。每个素材分组都会按活动、风格或用途来组织内容。',
    productChatTabsTitle: '聊天工作区',
    productChatTabsDescription:
      '这里是产品专属工作区，而不是通用聊天。当前线程会保持单一思路，Chats 则让你在这个产品和版本的已保存对话之间切换。',
    productChatNewTitle: '延伸到新的方向',
    productChatNewDescription:
      '使用新建聊天，在不覆盖当前对话的情况下开始新的探索。这是并排比较不同创意或策略方向的最快方式。',
    productChatInputTitle: '使用提及与上下文',
    productChatInputDescription:
      '这个输入框能够理解当前产品、版本和工作界面。你可以在聊天中直接提及素材、分组和其他参考内容，让 IMAI 基于精确上下文工作，而不只是依赖文本。',
    productVersionsTitle: '版本历史',
    productVersionsDescription:
      '每个版本都会保存产品的一个快照。创建新版本来探索不同方向，而不会丢失之前的工作。',
    mobileProductTabsTitle: '选择你的工作界面',
    mobileProductTabsDescription:
      '滑动这些标签页即可切换产品的不同工作模式。媒体工作室用于视觉内容，目录数据用于商品列表，印花与图案用于可重复设计探索，技术蓝图用于生产流程。',
    mobileProductChatTitle: '产品工作区聊天',
    mobileProductChatDescription:
      '点击这里可打开与该产品及当前界面绑定的聊天。你可以请求变体、风格调整或下一步建议，IMAI 会结合产品上下文进行回复。',
  },
};

const PRODUCT_STATIC_COPY: Record<SupportedLocale, ProductStaticCopy> = {
  en: EN_COPY,
  ar: AR_COPY,
  de: DE_COPY,
  es: ES_COPY,
  fr: FR_COPY,
  hi: HI_COPY,
  it: IT_COPY,
  ja: JA_COPY,
  ko: KO_COPY,
  ru: RU_COPY,
  zh: ZH_COPY,
  'zh-TW': ZH_COPY,
  vi: VI_COPY,
  id: ID_COPY,
};

export function getProductStaticCopy(locale: string): ProductStaticCopy {
  const copy = isSupportedLocale(locale)
    ? PRODUCT_STATIC_COPY[locale]
    : PRODUCT_STATIC_COPY[DEFAULT_LOCALE];

  return {
    ...copy,
    workspace: {
      ...EN_COPY.workspace,
      ...copy.workspace,
      logoReference: copy.workspace.logoReference
        ? {
            ...EN_COPY.workspace.logoReference,
            ...copy.workspace.logoReference,
          }
        : EN_COPY.workspace.logoReference,
    },
  };
}

type ReferenceOnlyVideoGenerationCopy = Required<
  Pick<
    ProductStaticCopy['generationSuggestions'],
    | 'referenceOnlyVideoTitle'
    | 'referenceOnlyVideoDescription'
    | 'referenceOnlyVideoGenerating'
    | 'referenceOnlyVideoStarted'
    | 'failedToStartVideoGeneration'
  >
>;

export function getProductGenerationSuggestionsCopy(
  locale: string
): ProductStaticCopy['generationSuggestions'] &
  ReferenceOnlyVideoGenerationCopy {
  const generationSuggestions =
    getProductStaticCopy(locale).generationSuggestions;

  return {
    ...generationSuggestions,
    referenceOnlyVideoTitle:
      generationSuggestions.referenceOnlyVideoTitle ??
      EN_COPY.generationSuggestions.referenceOnlyVideoTitle ??
      'Reference Video',
    referenceOnlyVideoDescription:
      generationSuggestions.referenceOnlyVideoDescription ??
      EN_COPY.generationSuggestions.referenceOnlyVideoDescription ??
      'Create a polished product film.',
    referenceOnlyVideoGenerating:
      generationSuggestions.referenceOnlyVideoGenerating ??
      EN_COPY.generationSuggestions.referenceOnlyVideoGenerating ??
      'Generating reference-only video...',
    referenceOnlyVideoStarted:
      generationSuggestions.referenceOnlyVideoStarted ??
      EN_COPY.generationSuggestions.referenceOnlyVideoStarted ??
      'Reference-only video generation started.',
    failedToStartVideoGeneration:
      generationSuggestions.failedToStartVideoGeneration ??
      EN_COPY.generationSuggestions.failedToStartVideoGeneration ??
      'Failed to start video generation.',
  };
}

export function formatProductStaticText(
  template: string,
  values: Record<string, string | number>
): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => {
    const value = values[key];
    return value === undefined ? '' : String(value);
  });
}

export function getProductAssetGroupLabel(
  locale: string,
  assetGroup: string
): string {
  const copy = getProductStaticCopy(locale).workspace;

  switch (assetGroup) {
    case 'main':
      return copy.main;
    case 'e-commerce-listing':
    case 'product-listing':
      return copy.productListing;
    case 'social-ads':
      return copy.socialMedia;
    case 'lifestyle':
      return copy.lifestyle;
    case 'detail':
      return copy.detail;
    case 'repeatable-pattern':
    case 'print-isolation':
    case 'certified-patterns':
      return copy.printsAndPatterns;
    case 'schematics':
    case 'schematic':
    case 'schematics-infographics':
      return copy.technicalBlueprints;
    case '3d-model':
    case '3d-models':
      return copy.threeDModels;
    default:
      return assetGroup
        .replace(/[-_]+/g, ' ')
        .trim()
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
  }
}
